import {AfterViewChecked, AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ChatPageContextService} from '../../../services/chat/chat-page-context.service';
import {ActivatedRoute, Router} from '@angular/router';
import {JsonPipe} from '@angular/common';
import {
  CheckResult,
  CheckResultControllerService,
  ProjectFile,
  WorkflowInstanceControllerService,
  WorkflowInstanceDto
} from '../../../api';
import {BimViewerService, TopicCreationData} from '../../../services/bim-viewer/bim-viewer.service';
import {StateConfigService} from '../../../services/state/state-config.service';
import {TaskDetailsService} from '../../../services/tasks/task-details.service';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicResultColumnComponent,
  BicResultComponent,
  BicResultHeaderComponent,
  BicResultRowComponent,
  TooltipDirective
} from '@buildinginformationcloud/bic-ui-components';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {
  faArrowDownLeftAndArrowUpRightToCenter,
  faArrowDownToBracket,
  faArrowUpRightAndArrowDownLeftFromCenter,
  faArrowUpRightFromSquare,
  faChartSimple,
  faCircle,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleQuestion,
  faCircleXmark,
  faClipboardCheck,
  faClipboardList,
  faCube,
  faDiagramProject,
  faFileExcel,
  faFilePdf,
  faList,
  faSparkles,
  faTable
} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {OverviewReportComponent} from '../../checks/overviewReport/overview-report.component';
import {
  applyIdsThresholdsToReport,
  DEFAULT_OVERALL_THRESHOLDS,
  IdsThresholds,
  statusFromIdsThresholds,
} from '../../../models/report/ids-thresholds';
import {
  dataTypeGroupOf,
  dataTypeGroupTooltip,
} from '../../checks/bimCheck/components/ids/models/ifc-data-type-groups';
import {
  NormalizedReport,
  ReportChip,
  ReportDetail,
  ReportFailureReason,
  ReportGroup,
  ReportRule,
  ReportStructure,
  StatusSummary,
  formatFailureReason,
  summarizeStatus
} from '../../../models/report/normalized-report.model';
import {cardinalityFallback, getReportAdapter} from '../../../models/report/check-report-adapter';
import {downloadCheckReportExcel} from '../../../models/report/report-excel-export';
import {downloadCheckReportPdf} from '../../../models/report/report-pdf-export';
import {fillMissingStepHeadings} from '../../../models/report/step-headings';
import {collectGuidFindings, ReportGuidFinding} from '../../../models/report/report-element-guids';
import {PageHeaderComponent} from '../../util/design/page-header/page-header.component';
import {WorkflowService} from '../../../services/workflow.service';
import {
  extractReportSelectionFromManifest,
  ReportDisplayConfig,
  ResolvedDetailEntry,
  resolveReportDisplayConfig,
  resolveReportDisplayConfigForRuleset,
  resolveReportDisplayConfigFromSnapshot,
} from '../../../models/report/report-display-config';
import {
  DetailColumn,
  formatDetailRating,
  isFacetDetail,
  resolveConstantFieldKeys,
  resolveDetailColumns,
  shouldShowRawOutput,
  splitDetailEntries,
} from '../../../models/report/report-detail-blocks';
import {
  aggregateColumnItems,
  averageRatedPercent,
  buildReportLeaves,
  columnItemValue,
  leafValue,
  leavesBefore,
  percentLabel,
  ReportColumnItem,
  ReportLeaf,
} from '../../../models/report/report-leaves';
import {
  computeIdsColumnWidths,
  computeRuleColumnWidths,
  RuleColumnKey
} from '../../../models/report/report-column-width';
import {slugKey} from '../../workflow-editor/workflow.models';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {RulesetRule, RulesetService} from '../../../services/ruleset.service';
import {
  activeFileLevelConfigKey,
  columnLabel,
  identifyingColumnKey,
  IDS_SPEC_DEFAULT_COLUMNS,
  IdsLevelKey,
  isColumnVisible,
  isLevelStatusVisible,
  levelLabel,
  levelLabelByKeys,
  orderedColumnKeys,
  orderedVisibleIdsColumns,
  ReportLevelKey,
  resolveIdsLevelKeys,
  resolveRenderedColumnKeys,
  resolveRenderedLevelKeys,
  secondaryColumnKeys,
} from '../../../models/report/report-levels';
import {Ruleset, RulesetType} from '../../ruleManagement/models/rule-management.models';

import {buildCardModel, CardModel, resolveCardLevelKeys,} from '../../../models/report/report-card-model';
import {
  buildReportFilterSections,
  EMPTY_REPORT_FILTER,
  filterReportLeaves,
  fromBicFilters,
  ReportFilterSection,
  ReportFilterState,
} from '../../../models/report/report-filter';
import {BicFilterComponent} from '../../util/design/bic-filter/bic-filter.component';
import {ReportCardTaskContext, ReportCardViewComponent} from './components/report-card-view/report-card-view.component';
import {ReportRowActionsComponent} from './components/report-row-actions/report-row-actions.component';
import {MarkdownModule} from 'ngx-markdown';
import {NormalizeMarkdownPipe} from '../../../pipes/normalize-markdown.pipe';
import {ReportSourceLinksPipe} from '../../../pipes/report-source-links.pipe';
import {ReportSourceLinkDirective} from '../../../directives/report-source-link.directive';

/** Die zwei Darstellungen des Prüfberichts. */
export type ReportViewMode = 'column' | 'list';

/** localStorage-Schlüssel der zuletzt gewählten Ansicht (pro Gerät, wie sidebarCollapsed). */
const VIEW_MODE_STORAGE_KEY = 'checkReportViewMode';

function readStoredViewMode(): ReportViewMode {
  return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'list' ? 'list' : 'column';
}

function storeViewMode(mode: ReportViewMode): void {
  localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
}

/**
 * Ein Teilbericht eines gemischten Laufs – genau ein Regelset.
 *
 * Entspricht einem Eintrag aus `outputData.reports[]` des `multi_check_executor`, angereichert um
 * das bereits normalisierte Berichtsmodell und die zugehörige Anzeige-Config.
 */
interface MixedReportEntry {
  rulesetId?: string;
  rulesetName: string;
  /** Anzahl der Regeln im Regelset; ersetzt die laufübergreifende Berichtssumme in der Overview. */
  ruleCount?: number;
  /** Typ des Teilberichts: IDS | AI | HYBRID – bestimmt den Adapter und die Darstellung. */
  checkType: string;
  status?: string;
  childInstanceId?: string;
  error?: string;
  report: NormalizedReport | null;
  displayConfig: ReportDisplayConfig | null;
  /**
   * Rohes Ergebnis des Sub-Runs. Bei HYBRID wird der Bericht daraus **nicht** direkt gebaut: dort
   * fächert der Lauf noch eine Ebene tiefer in Regel-Kinder auf, die einzeln geladen werden müssen
   * ({@link CheckReportPageComponent.enrichHybridMixedEntries}). Das Rohergebnis liefert dabei die
   * Kennzahlen des Sets (z. B. `score`).
   */
  rawReport?: any;
}

/** Aus der Regelset-Definition nachgeladene Metadaten einer Prüfregel. */
interface RuleMetadata {
  code?: string;
  description?: string;
}

function ruleMetadataKey(name: string | undefined): string {
  return (name ?? '').trim().toLocaleLowerCase('de-DE');
}

/**
 * Baut die Zuordnung Regelname → {Kürzel, Beschreibung} aus den geladenen Regelset-Regeln.
 * Regeln ohne beide Werte werden übersprungen, damit ein leerer Treffer keinen vorhandenen
 * Wert im Bericht überschreibt.
 */
function collectRuleMetadata(rules: ReadonlyArray<RulesetRule>): Map<string, RuleMetadata> {
  const metaByName = new Map<string, RuleMetadata>();
  for (const rule of rules) {
    const key = ruleMetadataKey(rule?.name);
    if (!key) continue;
    const code = rule.code?.trim() || undefined;
    const description = rule.description?.trim() || undefined;
    if (!code && !description) continue;
    const existing = metaByName.get(key);
    metaByName.set(key, {
      code: code ?? existing?.code,
      description: description ?? existing?.description,
    });
  }
  return metaByName;
}

/**
 * Liest den frei vergebenen Berichtsnamen einer Lauf-Instanz.
 *
 * Der Typ wird hier lokal erweitert statt aus dem generierten Modell gelesen: `src/app/api` ist
 * gitignored und wird im Build neu erzeugt, das Feld steht dort also erst, wenn das Backend im
 * jeweiligen Spec-Environment deployt ist. So baut das Frontend vorher und nachher, und ein
 * Bestandslauf (bzw. ein Backend ohne das Feld) liefert einfach `undefined` – dann greift der
 * bisherige Rückfall auf den Namen des Prüflaufs.
 */
function runReportName(instance: WorkflowInstanceDto): string | undefined {
  return (instance as WorkflowInstanceDto & { reportName?: string }).reportName;
}

@Component({
  selector: 'app-check-report-page',
  templateUrl: './check-report-page.component.html',
  standalone: true,
  imports: [
    BicBadgeComponent,
    BicResultComponent,
    BicResultColumnComponent,
    BicResultHeaderComponent,
    BicResultRowComponent,
    FaIconComponent,
    OverviewReportComponent,
    PageHeaderComponent,
    BicButtonComponent,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    TooltipDirective,
    JsonPipe,
    ReportCardViewComponent,
    ReportRowActionsComponent,
    BicFilterComponent,
    MarkdownModule,
    NormalizeMarkdownPipe,
    ReportSourceLinksPipe,
    ReportSourceLinkDirective,
  ],

  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./check-report-page.component.scss'],
})
export class CheckReportPageComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  /** Zurücksetzen der Filterauswahl beim Wechsel der Teilbericht-Seite (gemischter Lauf). */
  @ViewChild(BicFilterComponent) filterRef?: BicFilterComponent;
  /** Ergebnis-Spalte (Details) – siehe {@link suppressDetailRowSelection}. */
  @ViewChild('detailResultColumn', {read: ElementRef}) detailResultColumnRef?: ElementRef<HTMLElement>;

  protected readonly faExpandIcon = faArrowUpRightAndArrowDownLeftFromCenter;
  protected readonly faCollapseIcon = faArrowDownLeftAndArrowUpRightToCenter;

  loading = true;
  report: NormalizedReport | null = null;

  selectedViewMode: ReportViewMode = readStoredViewMode();
  activeFilter: ReportFilterState = EMPTY_REPORT_FILTER;

  /**
   * Identitätsbasierter Memo-Speicher für die vom Template gelesenen Ableitungen.
   *
   * Jeder dieser Getter wird in JEDEM Change-Detection-Zyklus aufgerufen, mehrere davon zusätzlich
   * je gerenderter Zeile und teils verschachtelt (Spaltenbreite → Items → Blätter). Ohne Memo
   * bedeutet ein Zyklus damit ein Vielfaches der Vollberechnung über den ganzen Bericht – bei
   * KI-Berichten mit tausenden Ergebnissen der dominante Kostenfaktor.
   *
   * Schlüssel sind Objekt-Identitäten: `report`, `reportDisplayConfig`, `activeFilter` und
   * `columnSelection` werden ausschließlich ersetzt, nie verändert. Die zwei Ausnahmen – das
   * nachträgliche Eintragen von Regel-Metadaten und Prüfschritt-Titeln in den bestehenden Bericht –
   * melden sich über {@link reportRevision}.
   */
  private memo = new Map<string, { deps: ReadonlyArray<unknown>; value: unknown }>();

  /** Zähler für In-Place-Änderungen am Bericht; Teil jedes Cache-Schlüssels (siehe {@link memo}). */
  private reportRevision = 0;

  /** Filterblöcke für `app-bic-filter`, aus den ungefilterten Blättern des Berichts. */
  get reportFilterSections(): ReportFilterSection[] {
    // Wird als Eingabe von `app-bic-filter` in jedem Zyklus gelesen und läuft dabei über die
    // UNGEFILTERTEN Blätter des ganzen Berichts – ohne Memo eine zweite Vollberechnung je Zyklus.
    return this.cached(
      'reportFilterSections',
      [this.report, this.reportRevision, this.reportDisplayConfig, this.currentCheckType],
      () => {
        const levelKeys = resolveCardLevelKeys(this.report, this.reportDisplayConfig, this.currentCheckType);
        const leaves = buildReportLeaves(this.report, levelKeys);
        return buildReportFilterSections(this.report, this.reportDisplayConfig, leaves, levelKeys);
      },
    );
  }

  /** Ebenen-Reihenfolge inkl. echter structure-Ebene; Ergebnis (detail) immer als Letztes. */
  get renderedLevelKeys(): ReportLevelKey[] {
    return this.cached('renderedLevelKeys', [this.report, this.reportDisplayConfig],
      () => resolveRenderedLevelKeys(this.report, this.reportDisplayConfig));
  }

  /** Keys der aufgeklappten Karten. Liegt hier, weil der Knopf in der Kopfzeile ihn mitsteuert. */
  expandedCardKeys = new Set<string>();
  private cardModelCache: {
    report: NormalizedReport | null;
    config: ReportDisplayConfig | null;
    checkType: string;
    filter: ReportFilterState;
    model: CardModel;
  } | null = null;

  protected readonly faTable = faTable;
  protected readonly faList = faList;
  protected readonly faChartSimple = faChartSimple;
  protected readonly faCircle = faCircle;

  selectedGroup: ReportGroup | undefined;
  selectedStructure: ReportStructure | undefined;
  selectedRule: ReportRule | undefined;
  currentCheckType = 'HYBRID';
  currentInstanceId: string | undefined;
  private readonly projectFileIdsByName = new Map<string, string>();
  private uniqueOpenableFileId: string | null = null;
  private workflowStatus: string | undefined;
  reportDisplayConfig: ReportDisplayConfig | null = null;
  /** Anzeigename des/der gewählten myCheck(s) für den Overview-Header (Feld „myCheck"). */
  rulesetDisplayName = '';
  /** Name der ausgeführten myCheck-Workflow-Instanz für den Overview-Header. */
  myCheckDisplayName = '';
  /**
   * Frei vergebener Berichtsname DIESES Laufs (`workflow_instance.report_name`); leer, wenn der
   * Lauf ohne Namen gestartet wurde – also bei allen Bestandsläufen.
   */
  reportRunName = '';
  /**
   * Individuelle Farbschwellen des IDS-Prüfregelsets; `null` = nicht konfiguriert, dann färbt der
   * Bericht wie bisher. Wird auch von PDF- und Excel-Export gebraucht.
   */
  idsThresholds: IdsThresholds | null = null;
  /** Aus der Berichtsliste übergebener myCheck-Name (Query-Param), falls keine Workflow-Definition auflösbar ist. */
  private myCheckNameFromRoute: string | undefined;
  /** Definition-ID der myCheck-Workflow-Instanz; macht „myCheck" im Overview-Header klickbar. */
  myCheckDefinitionId: string | undefined;
  /**
   * Der Lauf stammt aus dem Direktstart eines myChecks, sein Prüflauf wurde also automatisch
   * angelegt und ist für den Nutzer unsichtbar. Dann gibt es keinen Prüflauf zum Öffnen – der
   * Rück-Link zeigt nur auf das myCheck (siehe {@link rulesetRouterLinks}).
   */
  myCheckIsAutoGenerated = false;
  /** myCheck(s) des Berichts, klickbar im Overview-Header. */
  rulesetLinks: Array<{ id: string; name: string; type: RulesetType }> = [];
  /**
   * Teilberichte eines gemischten Laufs – ein Eintrag je Regelset. Leer bei einfachen Läufen.
   *
   * Die Ansicht bleibt dieselbe: Beim Wechsel der Stepper-Seite werden `report`,
   * `currentCheckType`, `reportDisplayConfig` und die Auswahlzustände aus dem jeweiligen Eintrag
   * neu gesetzt. Der komplette N-Ebenen-Renderer liest ausschließlich diese Felder und funktioniert
   * dadurch pro Seite unverändert.
   */
  mixedEntries: MixedReportEntry[] = [];
  /**
   * Projektdateien des Berichts, aus `CheckResult.projectFiles` – Kandidaten für das Ziel des
   * „Öffnen"-Buttons (Viewer). Bei einem IDS-Lauf über mehrere Dateien enthält das Array eine
   * Datei je Gruppe; siehe {@link projectFileForGroup}, das daraus die zur ausgewählten Gruppe
   * passende Datei auswählt.
   */
  private reportProjectFiles: ProjectFile[] = [];
  /**
   * IDs zentral bereitgestellter (read-only) Regelwerke. Sie werden im Bericht NICHT verlinkt –
   * genau wie in der Übersicht, wo eine bereitgestellte Kachel kein „Öffnen" anbietet, sondern
   * nur „Duplizieren" (siehe `ruleset-card.component.ts`). Über den Bericht ließ sich ein
   * bereitgestelltes Regelwerk sonst im Editor öffnen und überschreiben.
   *
   * Bewusst unabhängig von Adminrechten: die bereitgestellte Kopie wird im Admin-Bereich
   * gepflegt (`admin/rulesets`), nicht aus der Projektsicht heraus.
   */
  private readOnlyRulesetIds = new Set<string>();
  /** Der Prüflauf des Berichts ist zentral bereitgestellt und damit nicht zum Öffnen gedacht. */
  private myCheckIsReadOnly = false;
  /** Index des angezeigten Teilberichts innerhalb von {@link mixedEntries}. */
  activeMixedIndex = 0;
  /** Slug des Identifikator-Parameters (Hybrid) zum Gruppieren der Positionen; undefined ⇒ Fallback. */
  private hybridIdentifikatorSlug: string | undefined;
  protected readonly faCircleCheck = faCircleCheck;
  /** „25 %" bzw. „–" für unbewertete Ergebnisse – im Template gebraucht. */
  protected readonly percentLabel = percentLabel;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faCircleQuestion = faCircleQuestion;
  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faClipboardCheckLight = faClipboardCheck;
  protected readonly faArrowDownToBracket = faArrowDownToBracket;
  protected readonly faFileExcel = faFileExcel;
  protected readonly faFilePdf = faFilePdf;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faClipboardList = faClipboardList;
  // Icons der Regelset-Umschalter: je Prüfungsart ein eigenes Glyph, damit an der Kachel ohne
  // Tooltip erkennbar ist, welche Art von Prüfung dahinter steckt (siehe mixedEntryIcon).
  protected readonly faSparkles = faSparkles;
  protected readonly faDiagramProject = faDiagramProject;
  protected readonly faCube = faCube;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private chatPageContext = inject(ChatPageContextService);
  private checkResultService = inject(CheckResultControllerService);
  private instanceService = inject(WorkflowInstanceControllerService);
  private workflowService = inject(WorkflowService);
  private rulesetService = inject(RulesetService);
  private stateConfigService = inject(StateConfigService);
  private taskDetailsService = inject(TaskDetailsService);
  private bimViewerService = inject(BimViewerService);

  /** Human-readable label for die Overview, abgeleitet vom Prüfregeltyp. */
  get checkRuleTypeLabel(): string {
    switch (this.currentCheckType?.toUpperCase()) {
      case 'IDS':
        return 'IDS Prüfung';
      case 'HYBRID':
        return 'Hybride Prüfung';
      case 'HOAI':
      case 'KI':
      case 'AI':
        return 'KI Prüfung';
      case 'LOI':
        return 'LOI-Prüfung';
      case 'QUALITY':
        return 'Qualitätsprüfung';
      default:
        return 'Prüfung';
    }
  }

  /** Projekt des Berichts – Linkziel der Quellen-Badges im KI-Antworttext. */
  protected get sourceLinkProjectId(): number | null {
    return this.stateConfigService.getProjectId() || null;
  }

  /**
   * RouterLink-Kommandos zum myCheck-Workflow, aus dem dieser Bericht stammt; `null` = kein Ziel bekannt.
   *
   * <p>Auch `null` beim Direktstart eines myChecks: dessen Prüflauf wurde automatisch angelegt und
   * taucht in keiner Übersicht auf – der Link führte in einen Builder für etwas, das es für den Nutzer
   * nicht gibt. Das richtige Ziel ist dort das myCheck selbst, und das liefert
   * {@link rulesetRouterLinks} ohnehin schon.
   */
  get myCheckRouterLink(): any[] | null {
    const projectId = this.stateConfigService.getProjectId();
    if (!this.myCheckDefinitionId || !projectId || this.myCheckIsAutoGenerated) return null;
    // Zentral bereitgestellte Prüfläufe sind nur zum Ausführen oder Duplizieren gedacht; der
    // Builder fängt sie zwar selbst ab, aber ein Link, der nur in einen Toast läuft, ist kein Link.
    if (this.myCheckIsReadOnly) return null;
    // Gecacht, weil der Wert an [myCheckLink] haengt: ein neues Array je Aufruf
    // gilt fuer Angular in jedem Change-Detection-Durchlauf als geaendert (siehe
    // cached()). null ist ohnehin stabil.
    return this.cached('myCheckRouterLink', [projectId, this.myCheckDefinitionId],
      () => ['/projects', projectId, 'check-runs', 'builder', this.myCheckDefinitionId]);
  }

  downloadingReport = false;

  get canOpenInViewer(): boolean {
    return !!this.resolveOpenFileId();
  }

  /**
   * Excel-Export des Berichts (IDS, Hybrid und KI je mit eigenem Layout, siehe
   * `report-excel-export.ts`). Läuft vollständig im Frontend, kein Backend-Aufruf nötig.
   */
  async downloadReportAsExcel(): Promise<void> {
    if (!this.report || this.downloadingReport) return;
    this.downloadingReport = true;
    try {
      // Der PDF-Export braucht die Schwellwerte NICHT: er faerbt ausschliesslich ueber die
      // Statuswerte des Berichts, und die sind durch applyIdsThresholdsToReport schon umgefaerbt.
      // Das IDS-Excel-Blatt faerbt seine Ergebniszellen dagegen selbst aus dem Prozentwert.
      await downloadCheckReportExcel(this.report, this.reportDisplayConfig, this.currentCheckType,
        this.idsThresholds, this.exportRunName);
    } finally {
      this.downloadingReport = false;
    }
  }

  /**
   * PDF-Export des Berichts, aus demselben Karten-Datenmodell wie die Kartenansicht (siehe
   * `report-pdf-export.ts`). Läuft vollständig im Frontend, kein Backend-Aufruf nötig.
   */
  async downloadReportAsPdf(): Promise<void> {
    if (!this.report || this.downloadingReport) return;
    this.downloadingReport = true;
    try {
      // Der Gesamtwert und seine Ampelstufe kommen aus derselben Quelle wie der Kreis in der
      // Ansicht (#2317); die uebrigen Farben des PDF stammen aus den Statuswerten des Berichts,
      // die applyIdsThresholdsToReport bereits umgefaerbt hat.
      await downloadCheckReportPdf(this.report, this.reportDisplayConfig, this.currentCheckType,
        this.isIdsReport ? this.idsSingleResult.status : null, this.exportRunName);
    } finally {
      this.downloadingReport = false;
    }
  }

  get rulesOfSelectedGroup(): ReportRule[] {
    return this.selectedGroup?.rules ?? [];
  }

  get detailsOfSelectedRule(): ReportDetail[] {
    // IDS nutzt weiterhin selectedRule; der generische (KI/Hybrid) Renderer die Blatt-Selektion.
    if (this.isIdsReport) return this.selectedRule?.details ?? [];
    return this.selectedLeafDetails;
  }

  // ─── Perspektive (Hierarchie: files-first vs. rules-first) ─────────────────

  /** Erste (primäre) Nicht-Detail-Ebene laut Konfiguration – bestimmt die Hierarchie. */
  get primaryLevelKey(): 'files' | 'rules' {
    const first = this.orderedLevelKeys.find(key => key !== 'detail');
    return first === 'rules' ? 'rules' : 'files';
  }

  /** rules-first-Perspektive: oberste Ebene sind Prüfregeln, darunter die Dateien. */
  get isRulesFirst(): boolean {
    return this.primaryLevelKey === 'rules';
  }

  /** Zweite Nicht-Detail-Ebene (unter rules-first die Dateien, sonst die Prüfregeln). */
  get showSecondaryLevel(): boolean {
    return this.orderedLevelKeys.includes(this.isRulesFirst ? 'files' : 'rules');
  }

  /** Oberste Spalte: Dateien (files-first) oder aggregierte Prüfregeln (rules-first). */
  get displayGroups(): ReportGroup[] {
    if (!this.report) return [];
    return this.isRulesFirst ? this.invertedGroups : this.report.groups;
  }

  get secondaryEmptyText(): string {
    return this.isRulesFirst ? 'Bitte Prüfregel auswählen' : 'Bitte Datei auswählen';
  }

  /**
   * Items der untersten Gruppierungsebene vor dem Detailergebnis:
   * files-first → Prüfregeln, rules-first → Dateien. Fehlt die zweite Ebene,
   * wird die oberste Ebene selbst zusammengefasst.
   */
  private get summaryItems(): Array<{ status: 'success' | 'warning' | 'error' | 'unknown' }> {
    if (this.isIdsReport) {
      const specs = (this.report?.groups ?? []).flatMap(group => group.rules ?? []);
      return specs.length > 0 ? specs : (this.report?.groups ?? []);
    }
    // Generischer Renderer: feinste Ebene = die Regeln der Blätter.
    const leafRules = this.reportLeaves.map(leaf => leaf.rule);
    return leafRules.length > 0 ? leafRules : (this.report?.groups ?? []);
  }

  /** Statusverteilung der untersten Ebene für die Overview-Kreise. */
  get statusSummary(): StatusSummary {
    // IDS rechnet nicht über die Statusverteilung. Der Spec-Status ist dort ein BOOLEAN
    // (`spec_result === 1.0`), also zählte jede Spezifikation unter 100 % als Fehler und die
    // Overview zeigte konstant 0/0/100 – auch bei einem Dateiergebnis von 16 %.
    if (this.isIdsReport) return this.idsStatusSummary;

    const summary = summarizeStatus(this.summaryItems);
    // Ein erfolgreich beendeter Lauf ohne atomare Ergebniseinträge (z. B. ein leerer
    // Teilbericht) darf in der Overview nicht fälschlich als fehlgeschlagen erscheinen.
    return this.workflowStatus === 'COMPLETED' && this.summaryItems.length === 0
      ? {completed: 100, warning: 0, error: 0}
      : summary;
  }

  /**
   * Was im Overview-Header unter „Prüflauf" steht: der frei vergebene Berichtsname, sonst wie
   * bisher der Name des Prüflaufs bzw. des Regelwerks.
   */
  /**
   * Name für den Export-Dateinamen: der Prüflauf-Name, nicht der myCheck-Name – sonst ergeben
   * mehrere Läufe desselben myChecks identische Dateinamen (#2659). Der Name aus der Berichtsliste
   * (`?checkName=`) ist der Laufname; der myCheck-Name ist nur der letzte Rückfall.
   */
  private get exportRunName(): string {
    return this.reportRunName || this.myCheckNameFromRoute || this.myCheckDisplayName || '';
  }

  get runDisplayName(): string {
    return this.reportRunName || this.myCheckDisplayName || this.report?.meta.rulesetName || '';
  }

  /**
   * Tooltip auf dem Header-Namen: nennt den Prüflauf, sobald dort ein Berichtsname steht – sonst
   * wäre nicht mehr erkennbar, welcher Prüflauf gelaufen ist. Ohne Berichtsname kein Tooltip.
   */
  get runDisplayNameTooltip(): string {
    if (!this.reportRunName || !this.myCheckDisplayName) return '';
    return `Prüflauf: ${this.myCheckDisplayName}`;
  }

  /** Hinweis des Prüfmoduls, warum kein bewertbares Ergebnis vorliegt (IDS, „nicht anwendbar"). */
  get idsResultInfo(): string | null {
    return this.report?.meta.score == null ? (this.report?.meta.resultInfo ?? null) : null;
  }

  /**
   * myCheck(s) mit RouterLink-Kommandos zum passenden Editor.
   *
   * `routerLink: null` = zentral bereitgestelltes Regelwerk: der Name wird als reiner Text mit
   * Hinweis gerendert statt als Link in den Editor (siehe {@link readOnlyRulesetIds}).
   */
  get rulesetRouterLinks(): Array<{ id: string; name: string; routerLink: any[] | null }> {
    const projectId = this.stateConfigService.getProjectId();
    // readOnlyRulesetIds wird in place befuellt (add/clear), seine Referenz
    // aendert sich dabei nicht - als Abhaengigkeit zaehlt deshalb der Inhalt.
    // Sonst bliebe ein nachtraeglich als schreibgeschuetzt erkannter Regelsatz
    // im Cache faelschlich verlinkt.
    const readOnlyKey = [...this.readOnlyRulesetIds].join(',');
    // Gecacht, weil das Ergebnis an [rulesetLinks] haengt: map() erzeugt bei
    // jedem Aufruf ein neues Array aus neuen Objekten (siehe cached()).
    return this.cached('rulesetRouterLinks', [projectId, this.rulesetLinks, readOnlyKey], () => {
      if (!projectId) return [];
      const typePath: Record<RulesetType, string> = {KI: 'ki', HYBRID: 'hybrid', IDS: 'ids'};
      return this.rulesetLinks.map(link => ({
        id: link.id,
        name: link.name,
        routerLink: this.readOnlyRulesetIds.has(link.id)
          ? null
          : ['/projects', projectId, 'my-checks', link.id, typePath[link.type]],
      }));
    });
  }

  /** Regelanzahl des aktiven Teilberichts; bei gemischten Läufen nie über Regelsets aggregieren. */
  get overviewRuleCount(): number {
    return this.mixedEntries[this.activeMixedIndex]?.ruleCount ?? this.report?.meta.ruleCount ?? 0;
  }

  get detailEmptyText(): string {
    return this.isRulesFirst ? 'Bitte Datei auswählen' : 'Bitte Prüfregel auswählen';
  }

  private invertedGroupsCache: { source: ReportGroup[]; inverted: ReportGroup[] } | null = null;

  /** Invertierte Hierarchie (Prüfregel → Dateien) für die rules-first-Perspektive. */
  private get invertedGroups(): ReportGroup[] {
    const source = this.report?.groups ?? [];
    if (this.invertedGroupsCache?.source !== source) {
      this.invertedGroupsCache = {source, inverted: this.buildInvertedGroups(source)};
    }
    return this.invertedGroupsCache.inverted;
  }

  /**
   * Invertiert den normalisierten Baum Datei → Regel → Detail zu Regel → Datei → Detail.
   * Regeln werden über alle Dateien per Name aggregiert (Ergebnis = Mittelwert,
   * Status = schlechtester Datei-Status); die Details bleiben am Datei-Eintrag.
   */
  private buildInvertedGroups(groups: ReportGroup[]): ReportGroup[] {
    const byRule = new Map<string, ReportGroup>();
    for (const group of groups) {
      for (const rule of group.rules ?? []) {
        let inverted = byRule.get(rule.name);
        if (!inverted) {
          inverted = {name: rule.name, percent: null, status: 'unknown', rules: []};
          byRule.set(rule.name, inverted);
        }
        inverted.rules.push({
          name: group.name,
          percent: rule.percent,
          status: rule.status,
          details: rule.details,
          applicabilities: rule.applicabilities,
          elementCount: rule.elementCount,
        });
      }
    }

    const severity = {success: 0, warning: 1, unknown: 2, error: 3} as const;
    for (const inverted of byRule.values()) {
      const files = inverted.rules;
      // Nur bewertete Dateien mitteln (siehe averageRatedPercent): eine Datei ohne Ergebnis als
      // 0 % einzurechnen hätte die invertierte Sicht schlechter aussehen lassen als die normale.
      inverted.percent = averageRatedPercent(files.map(file => file.percent));
      inverted.status = files.reduce<'success' | 'warning' | 'error' | 'unknown'>(
        (worst, file) => severity[file.status] > severity[worst] ? file.status : worst,
        'success'
      );
    }
    return [...byRule.values()];
  }

  // ─── Generischer geordneter Renderer (3/4 Ebenen, KI + Hybrid) ───────────────
  // Datei[→Struktur]→Regel→Ergebnis, Reihenfolge aus der Config; Ergebnis immer zuletzt.

  /** Generische Selektion je Ebene (Name-basiert), z. B. { files: 'a.pdf', structure: 'Pos 1', rules: 'AL-01' }. */
  columnSelection: Record<string, string | undefined> = {};

  // ─── Kartenansicht ──────────────────────────────────────────────────────────

  /**
   * View-Modell der Kartenansicht, memoisiert über (Bericht, Config, Prüfungsart, Filter).
   *
   * Der Getter wird aus dem Template gelesen und damit in jedem Change-Detection-Zyklus
   * aufgerufen; `buildCardModel` läuft aber über den ganzen Bericht. Ohne diesen Cache wäre das
   * eine Vollberechnung je Zyklus.
   */
  get cardModel(): CardModel {
    const cache = this.cardModelCache;
    if (cache
      && cache.report === this.report
      && cache.config === this.reportDisplayConfig
      && cache.checkType === this.currentCheckType
      && cache.filter === this.activeFilter) {
      return cache.model;
    }
    const model = buildCardModel(
      this.report, this.reportDisplayConfig, this.currentCheckType, this.activeFilter,
    );
    this.cardModelCache = {
      report: this.report,
      config: this.reportDisplayConfig,
      checkType: this.currentCheckType,
      filter: this.activeFilter,
      model,
    };
    // Karten, die es nicht mehr gibt (Bericht- oder Filterwechsel), dürfen nicht „offen" bleiben.
    this.expandedCardKeys = new Set(
      [...this.expandedCardKeys].filter(key => model.itemKeys.includes(key)),
    );
    return model;
  }

  /** Ob ALLE Karten aufgeklappt sind – Beschriftung des Knopfes in der Kopfzeile. */
  get allCardsExpanded(): boolean {
    const keys = this.cardModel.itemKeys;
    return keys.length > 0 && this.expandedCardKeys.size === keys.length;
  }

  /** Nicht-Detail-Spalten in Reihenfolge. */
  get renderedColumnKeys(): Array<Exclude<ReportLevelKey, 'detail'>> {
    return this.cached('renderedColumnKeys', [this.report, this.reportDisplayConfig, this.currentCheckType],
      () => {
        const columns = resolveRenderedColumnKeys(this.report, this.reportDisplayConfig);
        if (!this.isBimBasisReport) return columns;
        return columns.filter(key => key !== 'rules');
      });
  }

  /**
   * Flache Blattliste Datei[→Struktur]→Regel als Basis für den Pivot – bereits gefiltert, damit die
   * Filterauswahl in der Spaltenansicht genauso greift wie in der Kartenansicht.
   */
  get reportLeaves(): ReportLeaf[] {
    return this.cached(
      'reportLeaves',
      [this.report, this.reportRevision, this.reportDisplayConfig, this.activeFilter],
      () => filterReportLeaves(
        buildReportLeaves(this.report, this.renderedColumnKeys), this.activeFilter,
      ),
    );
  }

  /** Ergebnis-Karten der voll selektierten Blätter (letzte Spalte). */
  get selectedLeafDetails(): ReportDetail[] {
    return this.cached('selectedLeafDetails', [this.reportLeaves, this.columnSelection], () => {
      const cols = this.renderedColumnKeys;
      return this.reportLeaves
        .filter(leaf => cols.every(key => leafValue(leaf, key) === this.columnSelection[key]))
        .flatMap(leaf => leaf.rule.details ?? []);
    });
  }

  // ─── Filter (wirkt in beiden Ansichten) ─────────────────────────────────────

  /** Konfigurierte, sichtbare Detail-Spalten (Schlüssel = benutzerdefinierte Ausgabeparameter). */
  get detailColumns(): DetailColumn[] {
    return this.cached('detailColumns', [this.reportDisplayConfig],
      () => resolveDetailColumns(this.reportDisplayConfig));
  }

  /** Spaltenbreiten (%) für die Dateien-/Struktur-/Prüfregeln-/Ergebnis-Spalten. */
  get ruleColumnWidths(): Partial<Record<RuleColumnKey, number>> {
    // Über columnFlex() je Spalte und Zyklus gelesen.
    return this.cached('ruleColumnWidths', [this.renderedLevelKeys, this.currentCheckType],
      () => computeRuleColumnWidths(this.renderedLevelKeys, this.isKiOrHybridCheck));
  }

  get idsOrderedLevelKeys(): IdsLevelKey[] {
    return resolveIdsLevelKeys(this.report, this.reportDisplayConfig);
  }

  get showSpecStatus(): boolean {
    return isLevelStatusVisible(this.reportDisplayConfig, 'specifications');
  }

  get showReqStatus(): boolean {
    return isLevelStatusVisible(this.reportDisplayConfig, 'requirements');
  }

  /** Sichtbare Spalten der IDS-Spezifikations-Ebene in konfigurierter Reihenfolge. */
  get idsSpecColumnKeys(): string[] {
    return orderedVisibleIdsColumns(
      this.reportDisplayConfig, ['specifications', 'rules'], IDS_SPEC_DEFAULT_COLUMNS,
    );
  }

  get idsSpecificationsHeaderLabel(): string {
    return levelLabelByKeys(this.reportDisplayConfig, ['specifications', 'rules'], 'Spezifikationen');
  }

  get showRawOutput(): boolean {
    return shouldShowRawOutput(this.currentCheckType);
  }

  get showFileResultColumn(): boolean {
    const levelKey = activeFileLevelConfigKey(this.reportDisplayConfig);
    return this.isColumnVisible(levelKey, 'status', true)
      || this.isColumnVisible(levelKey, 'rating', false);
  }

  get showDetailStatus(): boolean {
    return isLevelStatusVisible(this.reportDisplayConfig, 'detail');
  }

  get filesHeaderLabel(): string {
    return levelLabel(this.reportDisplayConfig, 'files');
  }

  get rulesHeaderLabel(): string {
    return levelLabel(this.reportDisplayConfig, 'rules');
  }

  get detailHeaderLabel(): string {
    // Bewusst nur der `detail`-Key und der Fallback „Prüfergebnis" (nicht das generische
    // „Ergebnis" aus LEVEL_DEFAULT_LABELS) – so hieß die Ergebnis-Spalte schon vorher.
    return levelLabelByKeys(this.reportDisplayConfig, ['detail'], 'Prüfergebnis');
  }

  isColumnItemSelected(levelKey: string, name: string): boolean {
    return this.columnSelection[levelKey] === name;
  }

  selectColumnItem(levelKey: string, name: string): void {
    const cols = this.renderedColumnKeys;
    const idx = cols.indexOf(levelKey as 'files' | 'structure' | 'rules');
    const next: Record<string, string | undefined> = {...this.columnSelection, [levelKey]: name};
    for (const key of cols.slice(idx + 1)) next[key] = undefined;
    this.columnSelection = next;
    this.autoSelectColumns();
  }

  /** Vorbelegung: je Spalte das erste Item wählen, sofern noch nichts/Ungültiges gewählt ist. */
  private autoSelectColumns(): void {
    let changed = false;
    const next = {...this.columnSelection};
    for (const key of this.renderedColumnKeys) {
      const items = this.columnItems(key);
      if (!next[key] || !items.some(item => item.name === next[key])) {
        next[key] = items[0]?.name;
        changed = true;
      }
    }
    if (changed) this.columnSelection = next;
  }

  /**
   * Feldschlüssel (lowercase), die über ALLE Prüfschritte der ausgewählten Regel denselben
   * Wert haben (Prüfparameter/Eigener Wert), im Gegensatz zu den je Schritt variierenden
   * KI-generierten Feldern.
   */
  get constantFieldKeys(): Set<string> {
    return this.detailBlocks.constantKeys;
  }

  /**
   * Aufbereitete Ergebnis-Spalte der ausgewählten Auswahl: je Prüfschritt die variierenden
   * Einträge, dazu die regelweit konstanten Einträge (Regelparameter) und die Schlüssel, über die
   * getrennt wurde.
   *
   * Bewusst EIN memoisierter Block statt dreier Getter: `resolveConstantFieldKeys` vergleicht alle
   * Felder aller Prüfschritte (JSON.stringify je Feld) und `splitDetailEntries` lief zuvor je
   * Detail-Zeile in jedem Change-Detection-Zyklus erneut – bei KI-Berichten mit ~46 Prüfschritten
   * je Regel der teuerste Teil der Ergebnis-Spalte.
   */
  get detailBlocks(): {
    steps: ReadonlyArray<{ detail: ReportDetail; entries: ResolvedDetailEntry[]; isFacet: boolean }>;
    constant: ResolvedDetailEntry[];
    constantKeys: Set<string>;
  } {
    return this.cached('detailBlocks', [this.detailsOfSelectedRule, this.detailColumns], () => {
      const details = this.detailsOfSelectedRule;
      const constantKeys = resolveConstantFieldKeys(details);
      const steps = details.map(detail => ({
        detail,
        entries: splitDetailEntries(detail, this.detailColumns, constantKeys).perStep,
        // Facetten-Zeile statt Prüfschritt-Kasten – dieselbe datengetriebene Unterscheidung wie in
        // der Kartenansicht (`report-card-model.ts`), damit beide Ansichten nicht divergieren.
        isFacet: isFacetDetail(detail),
      }));
      const first = details[0];
      const constant = first
        ? splitDetailEntries(first, this.detailColumns, constantKeys).constant
        : [];
      return {steps, constant, constantKeys};
    });
  }

  /**
   * Detail-Einträge, die für alle Prüfschritte der Regel konstant sind (Prüfparameter/Eigener
   * Wert). Einmalig unterhalb der Prüfschritt-Kästen dargestellt statt in jedem wiederholt.
   */
  get constantDetailEntries(): ResolvedDetailEntry[] {
    return this.detailBlocks.constant;
  }

  /** Distinct Items einer Spalte (Name/Status/% + konfigurierbare Zusatzwerte), aggregiert über die Blätter. */
  columnItems(levelKey: string): ReportColumnItem[] {
    return this.cached(
      `columnItems:${levelKey}`,
      [this.reportLeaves, this.renderedColumnKeys, this.columnSelection],
      () => {
        const cols = this.renderedColumnKeys;
        const scoped = leavesBefore(this.reportLeaves, cols, levelKey, this.columnSelection);
        return aggregateColumnItems(scoped, levelKey);
      },
    );
  }

  /**
   * Track-Key der generischen Spaltenzeilen.
   *
   * Für Prüfregeln gehört das Kürzel in den Key: wird es asynchron aus dem Regelset nachgeladen,
   * sortiert sich die Liste ggf. neu. Ein neuer Key erzwingt dann ein Remount der betroffenen
   * Tooltip-Hosts, statt eine bestehende Hover-Instanz auf ein anderes Listenelement mitzunehmen.
   *
   * Der Key hängt bewusst nur am Item: Angular 22 übersetzt einen track-Ausdruck, der die
   * Schleifenvariable `levelKey` der äußeren Schleife liest, in eine Funktion mit freier Variable
   * (`ReferenceError: levelKey_r9 is not defined`). Eindeutig bleibt er trotzdem, weil
   * `aggregateColumnItems` pro Ebene nach Namen zusammenfasst und nur Regel-Items ein Kürzel tragen.
   */
  columnItemTrackKey(item: ReportColumnItem): string {
    return `${item.shortCode ?? ''}::${item.name}`;
  }

  /** Regelname der aktuell selektierten Ergebnis-Spalte (für den Karten-Titel). */
  get selectedLeafRuleName(): string {
    return this.columnSelection['rules'] ?? '';
  }

  // ─── IDS-spezifischer 2-Spalten-Master-Detail-View ──────────────────────────

  get isIdsReport(): boolean {
    return this.currentCheckType?.toUpperCase() === 'IDS';
  }

  /**
   * Reiner BIM-Basis-Bericht: der Lauf bestand ausschließlich aus dem BIM-Basis-Check.
   *
   * Bewusst NUR für die Spaltendarstellung gedacht – dort ist die Regelspalte konstant
   * ("BIM-Basis-Check") und damit redundant. Für das Öffnen im Viewer ist dieser Getter das
   * falsche Kriterium: läuft der BIM-Basis-Check als Prüfregel innerhalb eines Hybrid-Regelsets,
   * meldet der Bericht `HYBRID` – siehe {@link activateOpenInViewer}.
   */
  get isBimBasisReport(): boolean {
    return this.currentCheckType?.toUpperCase() === 'BIM_BASIS';
  }

  /** Ergebnis-Details des Berichts, die konkrete Bauteile (IFC-GUIDs) benennen. */
  private get guidFindings(): ReportGuidFinding[] {
    return this.cached('guidFindings', [this.report], () => collectGuidFindings(this.report));
  }

  /**
   * Projektdatei einer Gruppe (Datei). Bei genau einer Datei im Bericht genügt sie unabhängig vom
   * Namen; bei mehreren Dateien (Multi-File-IDS-Lauf) muss der Name exakt zur Gruppe passen.
   * Bestimmt bei „Öffnen" nur noch, welche der gemeinsam geladenen Dateien als primäre (Titel-
   * gebende) Route dient – siehe {@link openInViewerPressed}, wo weiterhin ALLE Dateien geöffnet werden.
   */
  private projectFileForGroup(group: ReportGroup | undefined): ProjectFile | undefined {
    if (this.reportProjectFiles.length === 0) return undefined;
    if (this.reportProjectFiles.length === 1) return this.reportProjectFiles[0];
    return this.reportProjectFiles.find(file => file.name === group?.name);
  }

  /**
   * „Öffnen"-Button (Viewer): IDS-Berichte sowie jeder Bericht, dessen Ergebnisse sich auf konkrete
   * Bauteile beziehen lassen – jeweils mit mindestens einer auflösbaren Projektdatei.
   *
   * Kriterium ist die STRUKTUR (GUID-Listen im Roh-Output), nicht das Typ-Label: Der BIM-Basis-Check
   * meldet nur als eigenständiger Lauf `BIM_BASIS`; als Prüfregel eines Hybrid-Regelsets firmiert
   * sein Bericht als `HYBRID`, obwohl dieselben Spezifikationen mit denselben GUID-Listen
   * dargestellt werden (`specBasedSpecDetail` im Adapter bedient beide Wege).
   */
  get activateOpenInViewer(): boolean {
    const hasOpenTarget = this.reportProjectFiles.length > 0 || !!this.resolveOpenFileId();
    return !this.loading
      && (this.isIdsReport || this.guidFindings.length > 0)
      && hasOpenTarget;
  }

  /** Fester Typ-Marker fürs Topic-Label; fällt sonst auf den Tab-Namen (`rulesetDisplayName`) zurück. */
  private get openInViewerLabel(): string {
    switch (this.currentCheckType?.toUpperCase()) {
      case 'IDS': return 'IDS-Check';
      case 'QUALITY': return 'Qualitätscheck';
      default: return this.rulesetDisplayName || 'myCheck';
    }
  }

  /**
   * Übergibt die fehlgeschlagenen Anforderungen ALLER Gruppen (Dateien) als BCF-Topics an den
   * Viewer (ein Topic pro Anforderung) und öffnet alle geprüften Modelle als Koordinationsmodell.
   * Der Dateiname landet bewusst nicht in den BCF-Daten - der Viewer zeigt den Dateibezug selbst
   * über `modelIds` je Topic an.
   */
  openInViewerPressed(): void {
    if (!this.report) return;

    const topicInputs = this.isIdsReport
      ? this.buildIdsTopics(this.report)
      : this.buildGuidTopics();

    const primaryFile = this.resolvePrimaryProjectFile();
    if (!primaryFile) return;

    // Primäre Datei: die der aktuell ausgewählten/hervorgehobenen Gruppe (bestimmt Titel/Route im
    // Viewer), alle übrigen Dateien werden als Koordinationsmodell mitgeladen.
    const additionalFiles = this.reportProjectFiles.filter(file => file.id !== primaryFile.id);

    this.bimViewerService.open(primaryFile, topicInputs, additionalFiles,
      {reportId: this.routeReportId, reportType: this.routeReportType});
  }

  private resolvePrimaryProjectFile(): ProjectFile | null {
    const existing = this.projectFileForGroup(this.selectedGroup) ?? this.reportProjectFiles[0];
    if (existing?.id) return existing;
    // Fallback für Läufe, deren Projektdateien nicht als Liste am Bericht hängen (z. B. der
    // MIXED-Pfad, der vor dem Setzen von `reportProjectFiles` abzweigt): die ID wird aus dem
    // Lauf-Payload nachgeschlagen. Der Viewer braucht nur sie (`BimViewerService.open`).
    const fallbackId = this.resolveOpenFileId();
    if (!fallbackId) return null;
    return {
      id: fallbackId,
      name: this.selectedGroup?.name ?? this.columnSelection['files'] ?? this.report?.meta.fileName,
    } as ProjectFile;
  }

  private buildIdsTopics(report: NormalizedReport): TopicCreationData[] {
    const topicInputs: TopicCreationData[] = [];
    for (const group of report.groups) {
      for (const spec of group.rules) {
        const failedRequirements = spec.details.filter(req => (req.rawOutput?.guidListFail ?? []).length > 0);

        for (const req of failedRequirements) {
          // req.name ist optional (z. B. bei KI-Ausgaben ohne Facettenname) – ohne Fallback würde
          // das wörtliche "undefined" in der Topic-Beschreibung landen.
          const requirementName = req.name?.trim() || 'Anforderung';
          const guidListFail: string[] = req.rawOutput?.guidListFail ?? [];
          const guidListCorrect: string[] = req.rawOutput?.guidListCorrect ?? [];
          const sentence = guidListCorrect.length === 0
            ? 'kein Bauteil erfüllt'
            : `${guidListCorrect.length}/${guidListCorrect.length + guidListFail.length} Bauteile erfüllt`;

          topicInputs.push({
            title: `${spec.name}: ${requirementName}`,
            description: `${requirementName}: ${sentence}`,
            guids: guidListFail,
            labels: [this.openInViewerLabel],
            specification: spec.name,
            requirement: requirementName,
          });
        }
      }
    }
    return topicInputs;
  }

  /**
   * BCF-Topics aus allen Ergebnis-Details, die nicht erfüllte Bauteile benennen – ein Topic je
   * Detail (analog zur IDS-Vorgabe „ein Issue je Spezifikation").
   *
   * Typunabhängig, weil dieselbe Struktur in mehreren Berichtsformen auftritt: der BIM-Basis-Check
   * als eigenständiger Lauf ebenso wie als Prüfregel eines Hybrid-Regelsets. Die Labels tragen
   * deshalb den Regelnamen – im reinen BIM-Basis-Bericht ist das die konstante Regel
   * „BIM-Basis-Check", im Hybrid-Lauf die jeweilige Prüfregel.
   *
   * Der Dateiname bleibt wie beim IDS-Pfad bewusst aus den BCF-Daten heraus; den Dateibezug zeigt
   * der Viewer selbst anhand der GUIDs an.
   */
  private buildGuidTopics(): TopicCreationData[] {
    const topicInputs: TopicCreationData[] = [];
    for (const finding of this.guidFindings) {
      if (finding.guidListFail.length === 0) continue;

      const {detail, rule, guidListFail, guidListCorrect} = finding;
      // Name der geprüften Anforderung: im Roh-Output des Moduls steht er am Requirement, im
      // dargestellten Detail als erster Chip. Ohne Fallback landete "undefined" im Text.
      const requirementName = this.trimmedOrUndefined(detail.rawOutput?.name)
        ?? this.trimmedOrUndefined(detail.valueChips?.[0]?.value)
        ?? 'Anforderung';
      const specName = this.trimmedOrUndefined(detail.name)
        ?? this.trimmedOrUndefined(detail.facetLabel)
        ?? rule.name;
      const sentence = guidListCorrect.length === 0
        ? 'kein Bauteil erfüllt'
        : `${guidListCorrect.length}/${guidListCorrect.length + guidListFail.length} Bauteile erfüllt`;

      topicInputs.push({
        title: `${specName}: ${requirementName}`,
        description: `${requirementName}: ${sentence}`,
        guids: [...new Set(guidListFail)],
        labels: [this.openInViewerLabel],
        specification: specName,
        requirement: requirementName,
      });
    }

    return topicInputs;
  }

  private trimmedOrUndefined(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  /**
   * Spezifikationen der ausgewählten IDS-Datei - aus den GEFILTERTEN Blättern.
   *
   * Hier stand `this.selectedGroup?.rules ?? this.report?.groups?.[0]?.rules`, also der
   * Rohbericht. Damit sah die IDS-Spaltenansicht den Filter nie: die Auswahl im Filterpanel
   * änderte nichts, während dieselbe Auswahl in der Kartenansicht sauber griff (Bericht 2413).
   *
   * Der Filter ist blattbasiert (siehe report-filter.ts): er reduziert `reportLeaves`, und BEIDE
   * Ansichten sollen ausschließlich daraus aufgebaut werden - keine Ansicht bringt eigene
   * Filterlogik mit. Die generische Spaltenansicht hält sich über `columnItems()` daran; nur der
   * später ergänzte IDS-Zweig griff am Filter vorbei.
   *
   * Der Rückfall auf die erste Datei bleibt erhalten, bezieht sich jetzt aber auf die erste
   * SICHTBARE Datei - sonst zeigte die Spalte Spezifikationen einer Datei, die der Filter gerade
   * ausgeblendet hat.
   */
  get idsSpecifications(): ReportRule[] {
    return this.cached(
      'idsSpecifications',
      [this.reportLeaves, this.selectedGroup],
      () => {
        const groupName = this.selectedGroup?.name ?? this.idsFiles[0]?.name;
        const seen = new Set<string>();
        const specifications: ReportRule[] = [];
        for (const leaf of this.reportLeaves) {
          if (groupName != null && leaf.file?.name !== groupName) continue;
          const name = leaf.rule?.name ?? '';
          if (seen.has(name)) continue;
          seen.add(name);
          specifications.push(leaf.rule);
        }
        return specifications;
      },
    );
  }

  /** Stabiler Track-Key für IDS-Spezifikationen (Prüfregeln). */
  idsSpecificationTrackKey(spec: ReportRule): string {
    return `${spec.name ?? ''}::${spec.percent ?? ''}::${spec.status ?? ''}`;
  }

  /** Facetten (Requirements) der ausgewählten Spezifikation. */
  get idsRequirements(): ReportDetail[] {
    return this.selectedRule?.details ?? [];
  }

  /** Stabiler Track-Key für IDS-Anforderungen. */
  idsRequirementTrackKey(requirement: ReportDetail): string {
    return `${requirement.facetLabel ?? ''}::${requirement.name ?? ''}::${requirement.percent ?? ''}::${requirement.type ?? ''}`;
  }

  /**
   * Dateiebene für IDS (Datei -> Spezifikation -> Facette) - aus den GEFILTERTEN Blättern.
   *
   * Vorher `this.report?.groups`, also der Rohbericht. Ein Filter auf "Dateien" liess die Spalte
   * deshalb unverändert - genau die Beobachtung aus Bericht 2413 ("applying the filter to the
   * Dateien does not change anything in the view"). Begründung siehe {@link idsSpecifications}.
   */
  get idsFiles(): ReportGroup[] {
    return this.cached(
      'idsFiles',
      [this.reportLeaves],
      () => {
        const seen = new Set<string>();
        const files: ReportGroup[] = [];
        for (const leaf of this.reportLeaves) {
          const name = leaf.file?.name ?? '';
          if (seen.has(name)) continue;
          seen.add(name);
          files.push(leaf.file);
        }
        return files;
      },
    );
  }

  /**
   * ALLE sichtbaren Nicht-Status-Spalten einer Ebene in exakt gespeicherter Config-Reihenfolge –
   * inklusive der identifizierenden Spalte an IHRER konfigurierten Position (Status bleibt separat
   * die führende Icon-Spalte). Ohne Config (Altbericht) Fallback = [identifizierend, Ergebnis].
   */
  orderedColumnKeys(levelKey: string): string[] {
    // Je Ebene einmal pro Kopfzeile UND pro Datenzeile gelesen (verschachtelte @for im Template).
    return this.cached(`orderedColumnKeys:${levelKey}`, [this.reportDisplayConfig],
      () => orderedColumnKeys(this.reportDisplayConfig, levelKey));
  }

  /** Ob der Prüfregeltyp eine KI- oder Hybrid-Prüfung ist (Ergebnis-Spalte wird betont). */
  get isKiOrHybridCheck(): boolean {
    const type = this.currentCheckType?.toUpperCase();
    return type === 'HYBRID' || type === 'AI' || type === 'KI' || type === 'HOAI';
  }

  /**
   * Spaltenbreiten (als flex-grow-Anteil): KI-/Hybrid-Prüfungen betonen die
   * Ergebnis-Spalte mit 50%, die übrigen Spalten teilen sich die restlichen 50%
   * gleichmäßig (3 Spalten -> 25/25/50, 4 Spalten -> 16,7/16,7/16,7/50).
   * Für andere Prüftypen (kein Fokus auf Ergebnis) sind alle Spalten gleich breit.
   */
  columnFlex(levelKey: string): number {
    const width = this.ruleColumnWidths[levelKey as RuleColumnKey];
    if (width != null) return width;
    return 100 / (this.renderedColumnKeys.length + 1);
  }

  /** Spaltenbreiten (%) für die IDS-Spalten (Dateien/Spezifikationen/Facetten). */
  get idsColumnWidths(): Partial<Record<'files' | 'specifications' | 'requirements', number>> {
    return computeIdsColumnWidths(this.idsOrderedLevelKeys);
  }

  get showIdsFilesLevel(): boolean {
    return this.idsOrderedLevelKeys.includes('files');
  }

  get showIdsSpecificationsLevel(): boolean {
    return this.idsOrderedLevelKeys.includes('specifications');
  }

  get showIdsRequirementsLevel(): boolean {
    return this.idsOrderedLevelKeys.includes('requirements');
  }

  toggleCard(key: string): void {
    const next = new Set(this.expandedCardKeys);
    if (!next.delete(key)) next.add(key);
    this.expandedCardKeys = next;
  }

  toggleAllCards(): void {
    this.expandedCardKeys = this.allCardsExpanded
      ? new Set<string>()
      : new Set(this.cardModel.itemKeys);
  }

  setViewMode(mode: ReportViewMode): void {
    this.selectedViewMode = mode;
    storeViewMode(mode);
  }

  toggleViewMode(): void {
    this.setViewMode(this.selectedViewMode === 'column' ? 'list' : 'column');
  }

  createTaskForReportItem(levelKey: string, item: ReportColumnItem): void {
    const levelIndex = this.renderedColumnKeys.indexOf(levelKey as Exclude<ReportLevelKey, 'detail'>);
    const parentItems = this.renderedColumnKeys
      .slice(0, levelIndex)
      .map(key => this.columnSelection[key])
      .filter((name): name is string => !!name);
    const context = [...parentItems, item.name].join(' / ');
    this.openTaskDialogWithContext(`Überarbeiten notwendig bezüglich ${this.columnHeaderLabel(levelKey)}: ${context}`);
  }

  createTaskForReportDetail(detail: ReportDetail): void {
    const context = this.renderedColumnKeys
      .map(key => this.columnSelection[key])
      .filter((name): name is string => !!name)
      .join(' / ');
    this.openTaskDialogWithContext(
      `Überarbeiten notwendig${context ? ` für: ${context}` : ''}${detail.heading ? ` – ${detail.heading}` : ''}`,
      detail.text,
    );
  }

  createTaskForCardFacet({ruleName, facetName}: ReportCardTaskContext): void {
    this.openTaskDialogWithContext(`Überarbeiten notwendig für: ${ruleName} – ${facetName}`);
  }

  createTaskForIdsRequirement(requirement: ReportDetail): void {
    const context = [this.selectedGroup?.name, this.selectedRule?.name, requirement.facetLabel, requirement.name]
      .filter((value): value is string => !!value)
      .join(' / ');
    this.openTaskDialogWithContext(`Überarbeiten notwendig${context ? ` für: ${context}` : ''}`, requirement.text);
  }

  createTaskForIdsFile(group: ReportGroup): void {
    this.openTaskDialogWithContext(`Überarbeiten notwendig für: ${group.name}`);
  }

  createTaskForIdsSpecification(spec: ReportRule): void {
    const context = [this.selectedGroup?.name, spec.name].filter((value): value is string => !!value).join(' / ');
    this.openTaskDialogWithContext(`Überarbeiten notwendig${context ? ` für: ${context}` : ''}`);
  }

  private openTaskDialogWithContext(title: string, description?: string): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    const trimmedDescription = description?.trim();
    this.taskDetailsService.setOpenedTask({
      title,
      projectId,
      ...(trimmedDescription ? {description: trimmedDescription} : {}),
    });
  }

  copyDetailResponse(step: { detail: ReportDetail; entries: ResolvedDetailEntry[]; isFacet: boolean }): void {
    const detail = step.detail;
    const context = this.renderedColumnKeys
      .map(key => this.columnSelection[key])
      .filter((name): name is string => !!name)
      .join(' / ');
    const lines: string[] = [];
    if (context) lines.push(`Kontext: ${context}`);
    if (detail.heading) lines.push(`Prüfschritt: ${detail.heading}`);
    if (step.isFacet) {
      const facetName = [detail.facetLabel, detail.name].filter((value): value is string => !!value).join(': ');
      if (facetName) lines.push(`Facette: ${facetName}`);
      if (detail.count > 0) lines.push(`Bauteile: ${detail.count}`);
      if (detail.percent != null) lines.push(`Ergebnis: ${percentLabel(detail.percent)}`);
    }
    const responseEntries = step.entries
      .map(entry => (entry.label ? `${entry.label}: ${entry.value}` : entry.value).trim())
      .filter(Boolean);
    if (responseEntries.length > 0) {
      if (lines.length > 0) lines.push('');
      lines.push('Response:');
      lines.push(...responseEntries);
    } else if (detail.text?.trim()) {
      if (lines.length > 0) lines.push('');
      lines.push(`Response: ${detail.text.trim()}`);
    }
    if (this.showRawOutput && detail.rawOutput) {
      if (lines.length > 0) lines.push('');
      lines.push('Raw Output:');
      lines.push(JSON.stringify(detail.rawOutput, null, 2));
    }
    this.copyToClipboard(lines.join('\n'));
  }

  copyIdsRequirementSummary(requirement: ReportDetail): void {
    const lines = [
      `Datei: ${this.selectedGroup?.name || '–'}`,
      `Spezifikation: ${this.selectedRule?.name || '–'}`,
      `Facette: ${requirement.facetLabel || '–'}`,
      `Name: ${requirement.name || '–'}`,
    ];
    if (requirement.percent != null) lines.push(`Ergebnis: ${percentLabel(requirement.percent)}`);
    if (requirement.count > 0) lines.push(`Bauteile: ${requirement.count}`);
    this.copyToClipboard(lines.join('\n'));
  }

  copyIdsFileSummary(group: ReportGroup): void {
    const lines = [
      `Datei: ${group.name || '–'}`,
      `Ergebnis: ${percentLabel(group.percent)}`,
    ];
    this.copyToClipboard(lines.join('\n'));
  }

  copyIdsSpecificationSummary(spec: ReportRule): void {
    const lines = [
      `Datei: ${this.selectedGroup?.name || '–'}`,
      `Spezifikation: ${spec.name || '–'}`,
      `Ergebnis: ${percentLabel(spec.percent)}`,
    ];
    if (typeof spec.elementCount === 'number') lines.push(`Bauteile: ${spec.elementCount}`);
    this.copyToClipboard(lines.join('\n'));
  }

  private copyToClipboard(content: string): void {
    const text = content.trim();
    if (!text || !navigator?.clipboard?.writeText) return;
    navigator.clipboard.writeText(text);
  }

  /** Tooltip-Kopfzeile der Anforderungs-Badge: immer "Anforderungen". */
  facetTooltip(): string {
    return 'Anforderungen';
  }

  /**
   * Definition der Anforderung (Facette, ggf. Pset/Datentyp, Name, Kardinalität, ggf. Wert).
   * Jede Zeile erscheint nur, wenn ein Wert vorhanden ist; Pset/Datentyp nur bei der
   * Property-Facette, da die anderen Facetten kein Pset/Datentyp-Konzept haben.
   */
  facetTooltipSubtext(detail: ReportDetail): string {
    const raw: any = detail.rawOutput ?? {};
    const isProperty = (raw.facetType ?? '').toString().toLowerCase() === 'property';
    const lines: string[] = [];

    const facette = detail.facetLabel || raw.facetTypeLabel || raw.facetType;
    if (facette) lines.push(`Facette: ${facette}`);

    if (isProperty) {
      const pset = raw.propertySet || raw.pset || raw.propertySetName;
      if (pset) lines.push(`Pset: ${pset}`);
    }

    const name = detail.name || raw.name;
    if (name) lines.push(`Name: ${name}`);

    if (isProperty) {
      const datentyp = raw.dataType || raw.datatype || raw.propertyDataType;
      if (datentyp) lines.push(`Datentyp: ${datentyp}`);
      // Bei einer Datentypengruppe (#2312) schickt das Prüfmodul nur das Token; die enthaltenen
      // IFC-Datentypen stehen in derselben generierten Tabelle, die auch der Editor benutzt —
      // über 100 Namen je Anforderung mitzuschicken wäre reiner Payload.
      const gruppe = dataTypeGroupOf(raw.dataTypeGroup);
      // Gekürzt: „Zahl" umfasst über 100 Datentypen, die vollständige Liste würde den Tooltip
      // über den halben Bildschirm ziehen.
      if (gruppe) lines.push(`Zugelassen: ${dataTypeGroupTooltip(gruppe)}`);
    }

    const kardinalitaet = cardinalityFallback(raw.cardinality);
    if (kardinalitaet) lines.push(`Kardinalität: ${kardinalitaet}`);

    const hasExplicitValue = (Array.isArray(raw.values) && raw.values.length > 0)
      || (raw.value != null && (!Array.isArray(raw.value) || raw.value.length > 0));
    if (hasExplicitValue) {
      const wert = (detail.valueChips ?? [])
        .flatMap(chip => [chip.value, ...(chip.rest ?? [])])
        .filter(Boolean)
        .join(', ');
      if (wert) lines.push(`Wert: ${wert}`);
    }

    return lines.join('\n');
  }

  /**
   * Ein Fehlergrund als Klartext, z. B. „Datentyp weicht ab (46 Bauteile): IfcLabel erwartet,
   * IfcText gefunden".
   *
   * Bis auf diese Zeile war bei einem Fehlschlag nur „0 %" zu sehen – ohne Hinweis darauf, ob die
   * Eigenschaft ganz fehlt oder nur anders typisiert ist.
   */
  failureReasonText(reason: ReportFailureReason): string {
    return formatFailureReason(reason);
  }

  /** Tooltip-Kopfzeile der Anwendbarkeits-Badge: immer "Anwendbarkeit". */
  specApplicabilityTooltip(): string {
    return 'Anwendbarkeit';
  }

  /**
   * Vollständige Anwendbarkeit der Spezifikation: je Facette ein Block mit Facette/Name/Wert
   * (die Badge in der Tabelle zeigt nur die erste Facette + "+N"). Name fehlt bei Facetten ohne
   * eigenes Namenskonzept (Entität, Material). Zusätzlich ein Hinweis, wenn keine Bauteile im
   * Modell dazu passen (`notApplicable`).
   */
  specApplicabilityTooltipSubtext(spec: ReportRule): string {
    const chips = spec.applicabilities ?? [];
    const blocks = chips.length === 0
      ? ['Alle Bauteile']
      : chips.map(chip => {
        const lines: string[] = [];
        if (chip.label) lines.push(`Facette: ${chip.label}`);
        if (chip.name) lines.push(`Name: ${chip.name}`);
        const values = [chip.value, ...(chip.rest ?? [])].filter(Boolean).join(', ');
        if (values) lines.push(`Wert: ${values}`);
        return lines.join('\n');
      }).filter(Boolean);
    if (spec.notApplicable) {
      blocks.push('Hinweis: keine passenden Bauteile im Modell gefunden');
    }
    return blocks.join('\n\n');
  }

  onFiltersChange(raw: { [key: string]: any[] }): void {
    this.activeFilter = fromBicFilters(raw);
    // Die Spaltenansicht filtert über `reportLeaves`; eine bisher gewählte Spalten-Auswahl kann
    // dadurch weggefallen sein – dann würde die Ergebnis-Spalte leer bleiben.
    this.autoSelectColumns();
    this.dropFilteredAwayIdsSelection();
  }

  /**
   * Verwirft eine IDS-Auswahl, die der Filter gerade ausgeblendet hat.
   *
   * Notwendig, seit die IDS-Spalten aus den gefilterten Blättern kommen: {@link autoSelectColumns}
   * pflegt nur `columnSelection` (generische Ansicht), die IDS-Ansicht arbeitet dagegen mit
   * `selectedGroup`/`selectedRule`. Blendet der Filter die gewählte Datei aus, zeigte die
   * Spezifikations-Spalte sonst nichts mehr und die Facetten-Spalte weiterhin die ALTEN Facetten -
   * also Daten zu einer Zeile, die gar nicht mehr sichtbar ist.
   *
   * Bewusst nur zuruecksetzen und nicht neu auswaehlen: ohne Auswahl greift der vorhandene
   * Rueckfall auf die erste sichtbare Datei. Eine Auswahl zu erfinden, die der Nutzer nicht
   * getroffen hat, waere die schlechtere Antwort.
   */
  private dropFilteredAwayIdsSelection(): void {
    if (!this.isIdsReport) return;

    if (this.selectedGroup && !this.idsFiles.some(file => file.name === this.selectedGroup!.name)) {
      this.selectedGroup = undefined;
      this.selectedRule = undefined;
    }

    // Erst nach dem moeglichen Zuruecksetzen der Datei lesen - idsSpecifications haengt daran.
    if (this.selectedRule
      && !this.idsSpecifications.some(spec => spec.name === this.selectedRule!.name)) {
      this.selectedRule = undefined;
    }
  }

  /** Primärtext eines Chips (Label: Name, falls vorhanden, sonst Label: Wert). */
  chipText(chip: ReportChip): string {
    const value = chip.name || chip.value;
    return chip.label ? `${chip.label}: ${value}` : value;
  }

  /** Anzahl weiterer (nicht primär dargestellter) Chips/Werte für die "+N"-Anzeige. */
  chipExtraCount(chips: ReportChip[] | undefined, fromIndex: number): number {
    if (!chips || chips.length <= fromIndex) return 0;
    let extra = chips.length - fromIndex;
    for (let i = 0; i < Math.min(fromIndex, chips.length); i++) {
      extra += chips[i]?.rest?.length ?? 0;
    }
    return extra;
  }

  /**
   * `grid-template-columns` für Header UND Zeilen einer Nicht-Detail-Ebene – identisch, damit
   * Überschriften und Werte fluchten, unabhängig von der konfigurierten Spaltenreihenfolge.
   * Die identifizierende Spalte bekommt den flexiblen Rest (`minmax(0,1fr)`, truncatet),
   * jede Wertspalte (Ergebnis/Dateiformat/Kürzel) ist inhaltsbreit (`auto`). Die Beschreibung ist
   * Freitext beliebiger Länge und teilt sich den flexiblen Rest mit der identifizierenden Spalte,
   * statt die Zeile als `auto` aufzublähen.
   */
  columnGridTemplate(levelKey: string): string {
    // Wird im Template für den Kopf UND jede Zeile der Ebene gelesen, und zieht über
    // secondaryColumnWidth() die kompletten Spalten-Items nach – deshalb memoisiert.
    return this.cached(
      `columnGridTemplate:${levelKey}`,
      [this.columnItems(levelKey), this.reportDisplayConfig],
      () => this.buildColumnGridTemplate(levelKey),
    );
  }

  get showFilesLevel(): boolean {
    return this.orderedLevelKeys.includes('files');
  }

  get showRulesLevel(): boolean {
    return this.orderedLevelKeys.includes('rules');
  }

  get showDetailLevel(): boolean {
    return this.orderedLevelKeys.includes('detail');
  }

  /** Schlüssel des identifizierenden (immer sichtbaren) Werts je Ebene. */
  identifyingColumnKey(levelKey: string): string {
    return identifyingColumnKey(levelKey);
  }

  get showRuleResultColumn(): boolean {
    return this.isColumnVisible('rules', 'status', true)
      || this.isColumnVisible('rules', 'rating', true);
  }

  /**
   * Konfigurierte, sichtbare Zusatzspalten (ohne Status und ohne den identifizierenden Wert) einer
   * Ebene in gespeicherter Reihenfolge. Ohne Config (Altbericht) leer → nur der identifizierende
   * Wert + Status werden gezeigt.
   */
  secondaryColumnKeys(levelKey: string): string[] {
    return secondaryColumnKeys(this.reportDisplayConfig, levelKey);
  }

  get showDetailResult(): boolean {
    return this.isColumnVisible('detail', 'result', true);
  }

  get showDetailRating(): boolean {
    return this.isColumnVisible('detail', 'rating', true);
  }

  get orderedLevelKeys(): Array<'files' | 'rules' | 'detail'> {
    const configured = this.reportDisplayConfig?.levels;
    if (!configured || configured.length === 0) {
      return ['files', 'rules', 'detail'];
    }

    const seen = new Set<'files' | 'rules' | 'detail'>();
    const ordered: Array<'files' | 'rules' | 'detail'> = [];
    for (const level of configured) {
      const normalized = this.normalizeLevelKey(level.key);
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      ordered.push(normalized);
    }
    return ordered.length > 0 ? ordered : ['files', 'rules', 'detail'];
  }

  private cached<T>(key: string, deps: ReadonlyArray<unknown>, compute: () => T): T {
    const hit = this.memo.get(key);
    if (hit && hit.deps.length === deps.length && hit.deps.every((dep, i) => dep === deps[i])) {
      return hit.value as T;
    }
    const value = compute();
    this.memo.set(key, {deps, value});
    return value;
  }

  /**
   * Verwirft alle Ableitungen, nachdem der Bericht an sich selbst verändert wurde (Regel-Metadaten,
   * Prüfschritt-Titel). Ohne das bliebe die Ansicht auf dem Stand vor dem Nachladen stehen.
   */
  private invalidateReportCaches(): void {
    this.reportRevision++;
    this.memo.clear();
    this.cardModelCache = null;
  }

  private buildColumnGridTemplate(levelKey: string): string {
    const idKey = this.identifyingColumnKey(levelKey);
    return this.orderedColumnKeys(levelKey)
      .map(key => {
        if (key === idKey || key === 'description') return 'minmax(0, 1fr)';
        // Ergebnis ist immer "0–100 %": feste, schmale Breite statt der Zeichenlänge der
        // Überschrift ("Ergebnis" ist deutlich länger als jeder Wert), sonst laufen Überschrift
        // und Werte nicht bündig (siehe rating-Ausrichtung im Template).
        if (key === 'rating') return 'minmax(48px, 48px)';
        const width = this.secondaryColumnWidth(levelKey, key);
        // px statt ch: ch skaliert mit der Schriftgröße (Header text-xs, Zeile text-sm) und
        // ergäbe dort unterschiedliche Pixelbreiten.
        const px = width * CheckReportPageComponent.SECONDARY_COLUMN_PX_PER_CHAR;
        return `minmax(${px}px, ${px}px)`;
      })
      .join(' ');
  }

  private static readonly SECONDARY_COLUMN_PX_PER_CHAR = 8;

  /** Überschrift einer Zusatzspalte (aus der Config, sonst Default-Label des Keys). */
  secondaryColumnLabel(levelKey: string, key: string): string {
    if (key === 'shortCode') return 'Kürzel';
    return columnLabel(this.reportDisplayConfig, levelKey, key);
  }

  /** Anzeigewert einer Spalte (levelKey/columnKey) für ein Item. Leerer String, wenn kein Wert. */
  columnItemValue(levelKey: string, item: ReportColumnItem, key: string): string {
    return columnItemValue(item, levelKey, key);
  }

  /**
   * `?openViewer=1`: nach dem Laden direkt in den Viewer – der Weg, über den chatBIC „Zeig mir das
   * im Modell" anbietet. Gesetzt aus der Route, abgearbeitet in {@link ngAfterViewChecked}, sobald
   * der Bericht steht; die vielen Ladepfade haben keinen gemeinsamen Endpunkt.
   */
  private pendingOpenViewer = false;

  /** `?export=pdf|excel`: nach dem Laden einmal exportieren – der Exportlink aus chatBIC. */
  private pendingExport?: 'pdf' | 'excel';

  /** Kennung und Typ aus der Route – gehen an den Viewer, damit chatBIC dort weiß, was geladen ist. */
  private routeReportId?: string;
  private routeReportType?: string;

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const checkResultId = params['checkResultId'];
      if (checkResultId) {
        this.routeReportId = checkResultId;
        this.routeReportType = this.route.snapshot.queryParams['type']?.toUpperCase();
        const type = this.route.snapshot.queryParams['type']?.toUpperCase();
        this.myCheckNameFromRoute = this.route.snapshot.queryParams['checkName'] || undefined;
        this.loadReport(checkResultId, type);
      }
    });
    // Aufträge aus der Query (chatBIC-Links) abonniert statt aus dem Snapshot gelesen: steht der
    // Nutzer schon auf diesem Bericht, ändert ein Link nur die Query, nicht die Pfad-Parameter.
    this.route.queryParamMap.subscribe(query => {
      if (query.get('openViewer') === '1') this.pendingOpenViewer = true;
      const format = query.get('export');
      if (format === 'pdf' || format === 'excel') this.pendingExport = format;
    });
  }

  ngAfterViewInit(): void {
    this.suppressDetailRowSelection();
  }

  /** Zuletzt an chatBIC gemeldete Auswahl (Gruppe/Regel) – nur Änderungen werden gemeldet. */
  private reportedChatSelectionKey = '';

  /**
   * Meldet die im Bericht gewählte Gruppe und Regel an chatBIC (`ChatPageContextService`). „Warum ist
   * das fehlgeschlagen?" bezieht sich dann auf genau diese Regel. In ngAfterViewChecked, weil die
   * Auswahl aus vielen Ladepfaden und Klicks heraus gesetzt wird.
   */
  private reportSelectionToChat(): void {
    if (!this.routeReportId) return;
    const groupName = this.selectedGroup?.name;
    const ruleName = this.selectedRule?.name;
    const key = `${this.routeReportId}|${groupName ?? ''}|${ruleName ?? ''}`;
    if (key === this.reportedChatSelectionKey) return;
    this.reportedChatSelectionKey = key;
    this.chatPageContext.set('report', `/projects/${this.stateConfigService.getProjectId()}/reports/${this.routeReportId}`, {
      kind: 'report',
      reportId: this.routeReportId,
      reportType: this.routeReportType,
      groupName,
      ruleName,
    });
  }

  ngOnDestroy(): void {
    this.chatPageContext.clear('report');
  }

  ngAfterViewChecked(): void {
    this.reportSelectionToChat();
    if ((!this.pendingOpenViewer && !this.pendingExport) || this.loading || !this.report) return;
    const openViewer = this.pendingOpenViewer;
    const exportFormat = this.pendingExport;
    this.pendingOpenViewer = false;
    this.pendingExport = undefined;
    // Erst die Parameter aus der URL nehmen: sonst öffnete „Zurück" aus dem Viewer ihn sofort wieder
    // bzw. ein Reload exportierte erneut. Ohne Bauteilbezug (KI-Bericht) bleibt es beim Bericht.
    setTimeout(() => {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {openViewer: null, export: null},
        queryParamsHandling: 'merge',
        replaceUrl: true,
      }).then(() => {
        if (exportFormat === 'pdf') void this.downloadReportAsPdf();
        if (exportFormat === 'excel') void this.downloadReportAsExcel();
        if (openViewer && this.activateOpenInViewer) this.openInViewerPressed();
      });
    });
  }

  /** bic-result-column selektiert jede geklickte Zeile; Capture-Listener fängt das ab, außer beim eigenen Task-Button. */
  private suppressDetailRowSelection(): void {
    const column = this.detailResultColumnRef?.nativeElement;
    if (!column) return;
    column.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.report-detail-task-btn')) return;
      event.stopPropagation();
    }, {capture: true});
  }

  onGroupSelected(): void {
    this.selectedRule = undefined;
  }

  onIdsGroupSelected(): void {
    this.selectedRule = this.selectedGroup?.rules?.[0];
  }

  statusIcon(type: 'success' | 'warning' | 'error' | 'unknown') {
    switch (type) {
      case 'success':
        return this.faCircleCheck;
      case 'warning':
        return this.faCircleExclamation;
      case 'error':
        return this.faCircleXmark;
      case 'unknown':
        return this.faCircleQuestion;
    }
  }

  statusColor(type: 'success' | 'warning' | 'error' | 'unknown'): string {
    switch (type) {
      case 'success':
        return 'text-green';
      case 'warning':
        return 'text-yellow';
      case 'error':
        return 'text-red';
      case 'unknown':
        return 'text-gray-400';
    }
  }

  /**
   * Fixe Zusatzspaltenbreite für Header und alle Zeilen derselben Ebene. Andernfalls berechnet
   * jedes Grid sein `auto` separat und Überschrift und Werte laufen nicht bündig.
   */
  private secondaryColumnWidth(levelKey: string, key: string): number {
    const headerLength = this.secondaryColumnLabel(levelKey, key).length;
    const valueLength = this.columnItems(levelKey)
      .reduce((max, item) => Math.max(max, this.columnItemValue(levelKey, item, key).length), 0);
    return Math.max(headerLength, valueLength) + 1;
  }

  /** Überschrift einer Spalte (konfiguriert oder Default). */
  columnHeaderLabel(levelKey: string): string {
    return levelLabel(this.reportDisplayConfig, levelKey);
  }

  formatDetailRating(detail: ReportDetail): string | null {
    return formatDetailRating(detail);
  }

  /**
   * Schaltet den Bericht auf einen Teilbericht um: setzt genau die Felder, die der Renderer liest,
   * und verwirft die Spaltenauswahl der vorherigen Seite (Ebenen und Spalten unterscheiden sich je
   * Regelset).
   */
  selectMixedEntry(index: number): void {
    const entry = this.mixedEntries[index];
    if (!entry) return;
    this.activeMixedIndex = index;
    this.report = entry.report;
    this.currentCheckType = entry.checkType;
    this.reportDisplayConfig = entry.displayConfig;
    this.rulesetDisplayName = entry.rulesetName;
    this.rulesetLinks = entry.rulesetId
      ? [{id: entry.rulesetId, name: entry.rulesetName, type: this.rulesetTypeFromCheckType(entry.checkType)}]
      : [];
    this.idsThresholds = null;
    this.applyIdsThresholds(this.stateConfigService.getProjectId() ?? undefined);
    this.columnSelection = {};
    this.selectedGroup = undefined;
    this.selectedStructure = undefined;
    this.selectedRule = undefined;
    // Der neue Teilbericht hat eigene Dateien/Regeln – eine Auswahl aus dem vorigen würde dort
    // alles wegfiltern. Aufklappzustand gehört ebenfalls zum vorigen Bericht.
    this.resetFilter();
    this.expandedCardKeys = new Set<string>();
    this.updateAutoSelections();
  }

  private buildHybridRawData(parent: WorkflowInstanceDto, children: WorkflowInstanceDto[]): any {
    const outputData = parent.outputData ?? {};
    return {
      workflow_name: parent.workflowName ?? 'Hybrid-Prüfung',
      ruleset_name: parent.workflowName ?? outputData['ruleset_name'] ?? 'Hybrid-Prüfung',
      checker_name: parent.checkerName ?? outputData['checker_name'] ?? '',
      check_date: parent.completedAt ?? parent.startedAt,
      file_name: outputData['file_name'] ?? outputData['fileName'],
      rule_count: children.length,
      score: outputData['score'],
      identifikator_slug: this.hybridIdentifikatorSlug,
      // Jede Kind-Instanz = eine (Regel × Datei)-Kombination. Der Dateiname der Kind-Instanz
      // (aus ihren Trigger-Eingaben im State) erlaubt dem Adapter, eine Gruppe je Datei zu bilden.
      results: children.map(child => ({
        name: child.workflowName,
        status: child.status,
        file_name: this.hybridChildFileName(child),
        outputs: child.outputData ?? {},
      })),
    };
  }

  /**
   * Projektdateien eines Laufs, der über eine `workflowInstanceId` geladen wurde (Mixed-Lauf oder
   * IDS-Fallback ohne CheckResult) – Kandidaten für das Ziel des „Öffnen"-Buttons (Viewer), analog zu
   * `result.projectFiles` im CheckResult-Pfad (siehe {@link handleCheckResult}). Bei einem
   * Multi-File-IDS-Lauf enthält die Liste eine Datei je Gruppe (siehe {@link projectFileForGroup}).
   *
   * Die Trigger-Dateiauswahl steht nicht am `outputData` der Wurzelinstanz selbst, sondern je nach
   * Workflow-Variante an den `outputs` des Datei-Auswahl-Knotens im `state`, oder an `inputData`/
   * `outputData` eines einzelnen Steps (siehe `MyChecksExecutionComponent.loadSelectedFilesFromInstance`,
   * dieselben State-Pfade).
   */
  private resolveProjectFilesFromInstance(instance: WorkflowInstanceDto): ProjectFile[] {
    const candidateLists: unknown[] = [];
    const state: any = instance.state;
    if (state && typeof state === 'object') {
      for (const nodeId of Object.keys(state)) {
        const files = state[nodeId]?.outputs?.project_files;
        if (Array.isArray(files) && files.length > 0) candidateLists.push(files);
      }
    }
    const outputFiles = (instance.outputData as any)?.project_files;
    if (Array.isArray(outputFiles) && outputFiles.length > 0) candidateLists.push(outputFiles);

    if (Array.isArray(instance.steps)) {
      for (const step of instance.steps as any[]) {
        if (Array.isArray(step?.inputData?.project_files) && step.inputData.project_files.length > 0) {
          candidateLists.push(step.inputData.project_files);
        }
        if (Array.isArray(step?.outputData?.project_files) && step.outputData.project_files.length > 0) {
          candidateLists.push(step.outputData.project_files);
        }
      }
    }

    const files = candidateLists.find((entry): entry is any[] => Array.isArray(entry) && entry.length > 0);
    if (!files) return [];

    return files
      .map((file): ProjectFile | undefined => {
        if (!file || typeof file !== 'object') return undefined;
        // Nullish statt falsy prüfen: eine numerische ID `0` ist gültig und darf nicht wie eine
        // fehlende ID behandelt werden.
        const rawId = file.id ?? file.fileId;
        const id = rawId != null ? String(rawId).trim() : '';
        if (!id) return undefined;
        const rawName = file.name ?? file.fileName ?? file.filename;
        const name = typeof rawName === 'string' && rawName.trim() ? rawName.trim() : undefined;
        return {id, name};
      })
      .filter((file): file is ProjectFile => !!file);
  }

  /** Dateiname, gegen den eine Hybrid-Kind-Instanz (Regel-Lauf) geprüft wurde. */
  private hybridChildFileName(child: WorkflowInstanceDto): string | undefined {
    const pickName = (file: any): string | undefined => {
      if (!file || typeof file !== 'object') return undefined;
      const name = file['name'] ?? file['file_name'] ?? file['fileName'] ?? file['filename'];
      return name != null && String(name).trim() ? String(name).trim() : undefined;
    };
    const state: any = child.state ?? {};
    const fromFile = pickName(state['file']);
    if (fromFile) return fromFile;
    const projectFiles = state['project_files'];
    if (Array.isArray(projectFiles) && projectFiles.length > 0) {
      const fromProjectFile = pickName(projectFiles[0]);
      if (fromProjectFile) return fromProjectFile;
    }
    const output: any = child.outputData ?? {};
    return pickName(output);
  }

  private buildRawDataFromInstance(instance: WorkflowInstanceDto): any {
    const outputData = instance.outputData ?? {};
    return {
      ...outputData,
      workflow_name: instance.workflowName ?? 'Prüfung',
      ruleset_name: instance.workflowName ?? outputData['ruleset_name'] ?? 'Prüfung',
      checker_name: instance.checkerName ?? outputData['checker_name'] ?? '',
      check_date: instance.completedAt ?? instance.startedAt,
    };
  }

  /**
   * Overview-Kreise für IDS: nur noch für die Abwärtskompatibilität der drei Inputs vorhanden.
   *
   * Angezeigt wird stattdessen {@link idsSingleResult} — ein einzelner Gesamtkreis. Die drei
   * Kreise zeigen eine Statusverteilung, die es im IDS-Check nicht gibt: der Spec-Status ist
   * dort ein Boolean, weshalb hier `warning = 0` und `error = 100 − completed` erfunden werden
   * mussten. Das las sich als „50 % erfüllt, 50 % nicht erfüllt" statt als ein Ergebnis von 50 %.
   */
  private get idsStatusSummary(): StatusSummary {
    const completed = this.idsOverallPercent;
    if (completed == null) return {completed: 0, warning: 0, error: 0};
    return {completed, warning: 0, error: 100 - completed};
  }

  /**
   * Das Gesamtergebnis des IDS-Laufs in Prozent, oder `null`, wenn nichts bewertbar ist.
   *
   * <p>EINE Quelle für alle Darstellungen (Kreis, PDF, Excel): `meta.score` ist der vom Prüfmodul
   * über die **bewerteten** Dateien gemittelte Wert — genau die Rechnung aus dem Ticket (50 % +
   * 100 % + 0 % ⇒ 50 %). Eine nicht anwendbare Datei geht dort nicht als 0 % ein.
   *
   * <p>Der Rückfall auf den Mittelwert der Gruppen trägt Altberichte, deren `results`-JSON keinen
   * Score enthält.
   */
  get idsOverallPercent(): number | null {
    return this.report?.meta.score
      ?? averageRatedPercent((this.report?.groups ?? []).map(group => group.percent));
  }

  /**
   * Das Gesamtergebnis als ein Kreis mit Ampelfarbe (#2317).
   *
   * Die Stufen kommen aus dem Prüfregelset (#2309); ohne Konfiguration greift
   * {@link DEFAULT_OVERALL_THRESHOLDS}. Ein Default ist hier richtig — der Gesamtkreis ist neu,
   * es gibt kein bisheriges Verhalten, das sich ändern könnte.
   */
  get idsSingleResult(): { percent: number | null; status: 'success' | 'warning' | 'error' | 'unknown' } {
    const percent = this.idsOverallPercent;
    // Gecacht, weil das Objekt an [singleResult] haengt (siehe cached()).
    return this.cached('idsSingleResult', [percent, this.idsThresholds],
      (): { percent: number | null; status: 'success' | 'warning' | 'error' | 'unknown' } => {
        if (percent == null) return {percent: null, status: 'unknown'};
        const thresholds = this.idsThresholds ?? DEFAULT_OVERALL_THRESHOLDS;
        return {percent, status: statusFromIdsThresholds(percent, thresholds)};
      });
  }

  private handleCheckResult(result: CheckResult, requestedType?: string): void {
    let rawData: any = {};
    this.registerProjectFiles(result.projectFiles);
    if (result.results) {
      try {
        rawData = JSON.parse(result.results);
      } catch {
        rawData = {};
      }
    }
    this.registerProjectFilesFromPayload(rawData);

    const normalizedRequestedType = requestedType?.toUpperCase();
    const normalizedResultType = result.checkType?.toUpperCase();

    // Gemischter Lauf: ein Teilbericht je Regelset. Muss VOR der IDS-Heuristik unten greifen –
    // die prüft auf `specifications`, und ein IDS-Teilbericht innerhalb von reports[] würde den
    // Gesamtbericht sonst als einzelnen IDS-Bericht darstellen (nur ein Regelset sichtbar).
    if (normalizedResultType === 'MIXED' || Array.isArray(rawData?.reports)) {
      this.currentCheckType = 'MIXED';
      this.currentInstanceId = result.workflowInstanceId;
      const projectId = this.stateConfigService.getProjectId()!;
      if (result.workflowInstanceId) {
        this.instanceService.getInstance(result.workflowInstanceId, projectId).subscribe({
          next: instance => this.loadMyCheckDisplayName(instance.definitionId, instance.workflowName,
            runReportName(instance)),
          error: () => undefined,
        });
      }
      this.buildMixedReports(rawData, {
        checkerName: result.checker
          ? [result.checker.firstName, result.checker.lastName].filter(Boolean).join(' ')
          : undefined,
        checkDate: result.checkDate,
      }, projectId);
      return;
    }

    let checkRuleType = result.checkType ?? requestedType ?? 'HYBRID';
    // Some legacy My-Check IDS runs were stored with a non-IDS type.
    // If the user explicitly opens the report as IDS and the payload contains IDS structures,
    // force IDS rendering for a consistent view.
    const hasIdsShape = Array.isArray(rawData?.specifications)
      || (Array.isArray(rawData?.results)
        && rawData.results.some((entry: any) => {
          const candidate = entry?.outputs && typeof entry.outputs === 'object' ? entry.outputs : entry;
          return Array.isArray(candidate?.specifications);
        }));

    if (normalizedRequestedType === 'IDS'
      && normalizedResultType !== 'IDS'
      && hasIdsShape) {
      checkRuleType = 'IDS';
    }

    this.currentCheckType = checkRuleType;
    this.currentInstanceId = result.workflowInstanceId;
    this.reportProjectFiles = result.projectFiles ?? [];
    const adapter = getReportAdapter(checkRuleType);

    // Enrich with metadata
    if (!rawData.checker_name && result.checker) {
      rawData.checker_name = [result.checker.firstName, result.checker.lastName].filter(Boolean).join(' ');
    }
    if (!rawData.check_date && result.checkDate)
      rawData.check_date = result.checkDate;
    if (!rawData.file_name && result.projectFiles?.length) {
      rawData.file_name = result.projectFiles[0].name;
    }
    if (!rawData.ruleset_name && result.checkRules?.length) {
      rawData.ruleset_name = result.checkRules[0].name;
    }
    if (rawData.score == null && result.score != null) {
      rawData.score = result.score;
    }

    this.report = adapter.parseReportData(rawData);
    this.updateAutoSelections();
    // `loading` erst NACH der Auflösung der Spalten-Reihenfolge (reportDisplayConfig) auf false
    // setzen: sonst rendert der Bericht bereits mit reportDisplayConfig === null, und
    // resolveRenderedLevelKeys() greift auf die Dateien-vor-Regeln-Standardreihenfolge zurück,
    // bis die (asynchrone) Instanz-/Definitions-Auflösung nachträglich eintrifft.
    this.loadReportConfigFromWorkflowInstance(result.workflowInstanceId, checkRuleType, () => this.loading = false);
  }

  /**
   * @param allowCheckResultFallback Wenn keine Instanz zu der ID existiert, dieselbe ID als
   *   CheckResult versuchen. Nötig, weil die ID-Semantik vom Typ abhängt: die Berichtsliste
   *   schickt für alles außer IDS eine `workflowInstanceId` – fehlt die am CheckResult (Alt-/
   *   Importdaten), landete die Seite sonst auf „Kein Bericht gefunden", obwohl das Ergebnis da
   *   ist. Der IDS-Zweig hat diesen Rückfall in die andere Richtung schon lange.
   */
  private loadFromInstance(id: string, projectId: number, knownType?: string,
                           allowCheckResultFallback = true): void {
    this.instanceService.getInstance(id, projectId).subscribe({
      next: (instance: WorkflowInstanceDto) => {
        if (instance) {
          this.handleWorkflowInstance(instance, projectId, knownType);
        } else {
          this.loadFromCheckResultFallback(id, projectId, knownType, allowCheckResultFallback);
        }
      },
      error: (err) => {
        console.error('Failed to load workflow instance:', err);
        this.loadFromCheckResultFallback(id, projectId, knownType, allowCheckResultFallback);
      }
    });
  }

  /** Letzter Versuch: die ID als CheckResult lesen. Bricht ab, wenn der Weg schon probiert wurde. */
  private loadFromCheckResultFallback(id: string, projectId: number, knownType: string | undefined,
                                      allowed: boolean): void {
    if (!allowed) {
      this.loading = false;
      return;
    }
    this.checkResultService.getCheckResult(id, projectId).subscribe({
      next: (result: CheckResult) => {
        if (result) {
          this.handleCheckResult(result, knownType);
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private resetFilter(): void {
    this.activeFilter = EMPTY_REPORT_FILTER;
    this.filterRef?.clearAll();
  }

  // ─── Gemischter Lauf: ein Teilbericht je Regelset ─────────────────────────────

  /** True, wenn der Lauf mehrere Regelsets geprüft hat und der Stepper angezeigt wird. */
  get isMixedReport(): boolean {
    return this.mixedEntries.length > 0;
  }

  /**
   * Setzt `?type=` auf `MIXED`, sobald erkannt ist, dass es ein gemischter Lauf ist.
   *
   * Aus der Berichtsliste kommt hier `type=HYBRID` an: `SimpleCheck.checkRuleType` ist der
   * generierte Enum `CheckRuleType` (`LOI | IDS | HYBRID | AI`), in dem `MIXED` nicht existiert –
   * das Backend mappt deshalb `MIXED → HYBRID`. Für das Laden ist das korrekt (die ID ist dann eine
   * `workflowInstanceId`, und der echte Typ kommt aus `outputData.checkType`), der Link wäre aber
   * irreführend. Reine Adresskorrektur per `replaceUrl`: kein neuer History-Eintrag, und da nur
   * Query-Parameter wechseln, wird `loadReport` nicht erneut angestoßen.
   */
  private correctTypeQueryParam(): void {
    if (this.route.snapshot.queryParams['type'] === 'MIXED') return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {type: 'MIXED'},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private handleWorkflowInstance(instance: WorkflowInstanceDto, projectId: number, knownType?: string): void {
    const outputData = instance.outputData ?? {};
    this.registerProjectFilesFromPayload(instance.state);
    this.registerProjectFilesFromPayload(outputData);
    this.loadMyCheckDisplayName(instance.definitionId, instance.workflowName, runReportName(instance));
    this.workflowStatus = instance.status;
    this.applyOverviewSelection(outputData);

    // Selbstbeschreibender Output hat Vorrang; sonst der aus der Liste bekannte Typ.
    // Heuristik: wenn `specifications` vorhanden, ist es IDS (Abwärtskompatibilität älterer Läufe ohne checkType).
    const checkRuleType = outputData['checkType'] ?? outputData['check_type']
      ?? knownType
      ?? (Array.isArray(outputData['specifications']) ? 'IDS' : 'HYBRID');
    this.currentCheckType = checkRuleType;
    this.currentInstanceId = instance.id;
    this.reportProjectFiles = this.resolveProjectFilesFromInstance(instance);

    // Dispatcher-Lauf: ein Teilbericht je Regelset. Muss VOR der Adapter-Auflösung greifen –
    // getReportAdapter('MIXED') fiele sonst auf den Hybrid-Adapter zurück und würde die
    // reports[]-Struktur als Hybrid-Ergebnis fehlinterpretieren.
    //
    // Entscheidend ist die STRUKTUR, nicht das Typ-Label: seit ein Dispatcher-Lauf mit nur
    // einem Regelset-Typ auch diesen Typ meldet (z. B. 'IDS' statt 'MIXED'), würde eine Prüfung
    // allein auf 'MIXED' den IDS-Adapter auf eine reports[]-Struktur ansetzen – der kennt sie
    // nicht und der Bericht käme leer heraus.
    if (Array.isArray(outputData['reports']) || String(checkRuleType).toUpperCase() === 'MIXED') {
      this.buildMixedReports(instance.outputData ?? {}, {
        checkerName: instance.checkerName,
        checkDate: instance.completedAt ?? instance.startedAt,
        definitionId: instance.definitionId,
      }, projectId);
      return;
    }

    const adapter = getReportAdapter(checkRuleType);

    // Nur HYBRID aggregiert über Kind-Instanzen. AI/IDS/… nutzen den Instanz-Output direkt.
    if (checkRuleType?.toUpperCase() !== 'HYBRID') {
      const rawData = this.buildRawDataFromInstance(instance);
      this.report = adapter.parseReportData(rawData);
      this.updateAutoSelections();
      this.applyRuleMetadata(outputData, checkRuleType, projectId);
      this.applyStepInterpretations(this.collectRulesetIds(outputData), checkRuleType, projectId);
      // `loading` erst NACH der Auflösung der Spalten-Reihenfolge (reportDisplayConfig) auf false
      // setzen: sonst rendert der Bericht bereits mit reportDisplayConfig === null, und
      // resolveRenderedLevelKeys() greift auf die Dateien-vor-Regeln-Standardreihenfolge zurück,
      // bis die (asynchrone) Definitions-Auflösung nachträglich eintrifft.
      this.applyReportDisplayConfigFromInstance(instance, checkRuleType, () => this.loading = false);
      return;
    }

    this.instanceService.getChildInstances(instance.id!, projectId).subscribe({
      next: (children: WorkflowInstanceDto[]) => {
        children.forEach(child => {
          this.registerProjectFilesFromPayload(child.state);
          this.registerProjectFilesFromPayload(child.outputData);
        });
        // Identifikator-Slug aus einer Regel-Definition auflösen (zum Gruppieren der Positionen),
        // dann erst den Bericht bauen/parsen.
        this.resolveHybridIdentifikatorSlug(children, projectId).subscribe(slug => {
          this.hybridIdentifikatorSlug = slug;
          const rawData = this.buildHybridRawData(instance, children);
          this.report = adapter.parseReportData(rawData);
          this.updateAutoSelections();
          this.applyRuleMetadata(outputData, checkRuleType, projectId);
          this.applyReportDisplayConfigFromInstance(instance, checkRuleType, () => this.loading = false);
        });
      },
      error: () => {
        // No children – use outputData directly
        const rawData = this.buildRawDataFromInstance(instance);
        this.report = adapter.parseReportData(rawData);
        this.updateAutoSelections();
        this.applyRuleMetadata(outputData, checkRuleType, projectId);
        this.applyReportDisplayConfigFromInstance(instance, checkRuleType, () => this.loading = false);
      }
    });
  }

  /**
   * Baut Hybrid-Teilberichte aus den Regel-Kindern **ihres Sub-Runs** neu auf.
   *
   * Ein hybrider Sub-Run fächert selbst noch einmal auf: je Prüfregel eine eigene Instanz. Die
   * Positionen und Ergebnis-Karten des Berichts entstehen erst aus diesen Kindern – das aggregierte
   * Ergebnis des Sub-Runs allein reicht nicht (dort fehlen u. a. die Regelnamen).
   *
   * Wichtig: Die Kinder hängen an der **Sub-Run**-Instanz, nicht an der Wurzel. Der Einzellauf-Pfad
   * fragt die Wurzel ab – bei einem gemischten Lauf wären das die Sub-Runs selbst, nicht die Regeln.
   */
  private enrichHybridMixedEntries(
    meta: { checkerName?: string; checkDate?: any },
    projectId: number,
  ): void {
    this.mixedEntries.forEach((entry, index) => {
      if (entry.checkType !== 'HYBRID' || !entry.childInstanceId) return;
      this.instanceService.getChildInstances(entry.childInstanceId, projectId).subscribe({
        next: (children: WorkflowInstanceDto[]) => {
          if (!children?.length) return;
          this.resolveHybridIdentifikatorSlug(children, projectId).subscribe(slug => {
            const rawData = {
              ruleset_name: entry.rulesetName,
              checker_name: meta.checkerName ?? '',
              check_date: meta.checkDate,
              rule_count: children.length,
              score: entry.rawReport?.['score'],
              identifikator_slug: slug,
              results: children.map(child => ({
                name: child.workflowName,
                status: child.status,
                outputs: child.outputData ?? {},
              })),
            };
            const rebuilt = getReportAdapter('HYBRID').parseReportData(rawData);
            this.mixedEntries = this.mixedEntries.map((candidate, i) =>
              i === index ? {...candidate, report: rebuilt} : candidate);
            // Wird die Seite gerade angezeigt, neu anwenden, damit der Bericht sofort erscheint.
            if (index === this.activeMixedIndex) this.selectMixedEntry(index);
          });
        },
        // Ohne Kinder bleibt der aus dem Sub-Run-Ergebnis gebaute Bericht stehen.
        error: () => undefined,
      });
    });
  }

  viewLogs(): void {
    if (!this.currentInstanceId) return;
    const projectId = this.stateConfigService.getProjectId();
    const route = this.router.createUrlTree(
      ['projects', projectId, 'check-runs', 'log'],
      {queryParams: {instanceId: this.currentInstanceId}},
    );
    this.router.navigateByUrl(route);
  }

  openInViewer(): void {
    const fileId = this.resolveOpenFileId();
    const projectId = this.stateConfigService.getProjectId();
    if (!fileId || !projectId) return;
    const route = this.router.createUrlTree(['projects', projectId, 'files', fileId]);
    window.open(this.router.serializeUrl(route), '_blank');
  }

  /** Lädt die Regelanzahl je Regelset, da der Ergebnis-Output mehrere Sets aggregieren kann. */
  private loadMixedRuleCounts(projectId: number): void {
    this.rulesetService.listRulesets(projectId).subscribe({
      next: ({prebuilt, custom}) => {
        this.collectReadOnlyRulesetIds([...prebuilt, ...custom]);
        const countsById = new Map([...prebuilt, ...custom].map(ruleset => [ruleset.id, ruleset.ruleCount]));
        this.mixedEntries = this.mixedEntries.map(entry => ({
          ...entry,
          ruleCount: countsById.get(entry.rulesetId ?? '') ?? entry.ruleCount,
        }));
      },
      error: () => undefined,
    });
  }

  /**
   * Holt die Berichtsstruktur je Regelset aus der myCheck-Definition nach – ein Request für alle
   * Teilberichte. Greift nur, wo der Lauf-Snapshot keine Config mitgebracht hat (z. B. IDS-Sub-Runs,
   * die keine `effectiveReportConfig` erzeugen).
   */
  private applyMixedDisplayConfigsFromDefinition(definitionId: string | undefined, projectId: number): void {
    if (!definitionId) return;
    this.workflowService.getDefinition(definitionId, projectId).subscribe({
      next: (definition) => {
        this.mixedEntries = this.mixedEntries.map(entry => entry.displayConfig
          ? entry
          : {
            ...entry,
            displayConfig: resolveReportDisplayConfigForRuleset(
              definition.manifestJson, entry.rulesetId, entry.checkType),
          });
        // Die aktive Seite neu anwenden, damit die nachgeladene Struktur sofort greift.
        this.selectMixedEntry(this.activeMixedIndex);
      },
      // Ohne Config rendert der Bericht mit den Standard-Ebenen – kein harter Fehler.
      error: () => undefined,
    });
  }

  /**
   * Regel-Metadaten (Kürzel + Beschreibung) je Teilbericht nachladen – jeder Eintrag kennt sein
   * eigenes Regelset.
   *
   * Der Einzellauf-Pfad ({@link applyRuleMetadata}) leitet die Regelset-IDs aus dem
   * Instanz-Output ab; bei einem gemischten Lauf steht dort nur die Aggregation, weshalb hier
   * direkt über die Einträge gegangen wird.
   */
  private applyMixedRuleMetadata(projectId: number): void {
    if (!projectId) return;
    const targets = this.mixedEntries
      .map((entry, index) => ({entry, index}))
      .filter(({entry}) => !!entry.rulesetId && !!entry.report && entry.checkType !== 'IDS');
    if (targets.length === 0) return;

    for (const {entry, index} of targets) {
      const type = entry.checkType === 'HYBRID' ? 'HYBRID' : 'KI';
      this.rulesetService.getRulesetRules({id: entry.rulesetId, type} as unknown as Ruleset, projectId)
        .pipe(catchError(() => of([])))
        .subscribe(rules => {
          const metaByName = collectRuleMetadata(rules);
          if (metaByName.size === 0) return;
          this.applyRuleMetadataToReport(entry.report, metaByName);
          // Wird die betroffene Seite gerade angezeigt, die Ansicht neu anwenden.
          if (index === this.activeMixedIndex) this.report = entry.report;
        });
    }
  }

  /**
   * Icon eines Teilbericht-Umschalters, je Prüfungsart unterschiedlich.
   *
   * `AI` und `KI` bezeichnen dieselbe Prüfungsart – die Kette liefert je nach Quelle das eine oder
   * das andere Kürzel, deshalb werden beide behandelt (wie in {@link mixedEntryTypeLabel}).
   * Unbekannte Arten fallen auf das IDS-Glyph zurück, damit der Umschalter nie ohne Icon dasteht.
   */
  mixedEntryIcon(entry: MixedReportEntry): IconDefinition {
    switch (entry.checkType) {
      case 'AI':
      case 'KI':
        return this.faSparkles;
      case 'HYBRID':
        return this.faDiagramProject;
      case 'BIM_BASIS':
      case 'QUALITY':
        // Eigenes Glyph, damit der Umschalter den mitlaufenden BIM-Basis- bzw. Qualitätscheck
        // nicht wie einen weiteren IDS-Teilbericht aussehen lässt.
        return this.faClipboardCheckLight;
      case 'IDS':
      default:
        return this.faCube;
    }
  }

  /** Ausgeschriebener Prüfungstyp eines Teilberichts – als Tooltip am Umschalter. */
  mixedEntryTypeLabel(entry: MixedReportEntry): string {
    switch (entry.checkType) {
      case 'IDS':
        return 'IDS Prüfung';
      case 'HYBRID':
        return 'Hybride Prüfung';
      case 'AI':
      case 'KI':
        return 'KI Prüfung';
      case 'BIM_BASIS':
        return 'BIM-Basis-Check';
      case 'QUALITY':
        // Produktname der Prüfung – wie in der Seitenleiste und in check-type.pipe.ts.
        return 'All-Right Check';
      default:
        return entry.checkType;
    }
  }

  /**
   * Lädt die Regel-Metadaten (Kürzel + Beschreibung) aus dem/den Regelset(s) nach und trägt sie als
   * `ReportRule.shortCode` / `ReportRule.description` in den Bericht ein. Beide stehen nicht im
   * Prüfergebnis, sondern nur im Regelset (`ruleset.rules[].configJson.ruleShortCode` bzw.
   * `rules[].description`); Zuordnung über den Regelnamen.
   */
  private applyRuleMetadata(outputData: any, checkRuleType: string, projectId: number): void {
    const rulesetIds = this.collectRulesetIds(outputData);
    if (rulesetIds.length === 0 || !projectId) return;
    // Hinweis: Prüfschritt-Überschriften kommen NICHT hierher, sondern aus
    // `applyStepInterpretations` – primär aus dem Prüfmodell selbst, als Lückenfüller aus den
    // Titeln des Prüfprompts. Die interpret_rule-Sätze („aiStepInterpretation") taugen nur als
    // letzte Reserve, weil sie ganze Sätze statt kurzer Titel liefern.
    const type = (checkRuleType ?? '').toUpperCase() === 'HYBRID' ? 'HYBRID' : 'KI';
    forkJoin(
      rulesetIds.map(id =>
        this.rulesetService.getRulesetRules({id, type} as unknown as Ruleset, projectId)
          .pipe(catchError(() => of([])))),
    ).subscribe(lists => {
      const metaByName = collectRuleMetadata(lists.flat());
      if (metaByName.size > 0) this.applyRuleMetadataToReport(this.report, metaByName);
    });
  }

  /** Sammelt die relevanten Regelset-IDs aus dem Instanz-Output (dedupliziert). */
  private collectRulesetIds(outputData: any): string[] {
    const ids = new Set<string>();
    const eff = outputData?.effectiveReportConfig?.rulesetId;
    if (typeof eff === 'string' && eff) ids.add(eff);
    const configs = outputData?.reportConfig?.reportConfigs;
    if (Array.isArray(configs)) {
      for (const cfg of configs) {
        if (typeof cfg?.rulesetId === 'string' && cfg.rulesetId) ids.add(cfg.rulesetId);
      }
    }
    if (typeof outputData?.ruleset_id === 'string' && outputData.ruleset_id) ids.add(outputData.ruleset_id);
    return [...ids];
  }

  /**
   * KI: Prüfschritte ohne Überschrift aus den Titeln des Prüfprompts nachfüllen.
   *
   * Das Prüfmodell liefert je Antwort einen Titel (`check_parameter`), lässt ihn aber nicht
   * zuverlässig gefüllt – und wenn keine andere Antwort derselben Regel einen Titel hat, kann der
   * `AiReportAdapter` auch nichts abschreiben. Die Titel des Regelsets (aus dem Prüfprompt, siehe
   * `getKiStepTitles`) schließen genau diese Lücke: set-weit, stabil, ohne KI-Aufruf zur Anzeige.
   *
   * Bewusst nur Lückenfüller, kein Überschreiben – der Titel des Modells ist spezifischer. Und
   * bewusst je Prüfschritt statt je Listenindex: im Positions-Modus hängen alle Positionen in
   * einer flachen Detailliste (siehe {@link fillMissingStepHeadings}). Nicht für HYBRID/IDS.
   */
  private applyStepInterpretations(rulesetIds: string[], checkRuleType: string, projectId: number): void {
    if ((checkRuleType ?? '').toUpperCase() === 'HYBRID' || this.isIdsReport) return;
    if (rulesetIds.length === 0 || !projectId) return;
    forkJoin(
      rulesetIds.map(id => this.rulesetService.getKiStepTitles(id).pipe(catchError(() => of([] as string[])))),
    ).subscribe(lists => {
      // Prüfschritte sind set-weit (gemeinsamer Prüfprompt) → erste nicht-leere Titelliste nutzen.
      const titles = (lists.find(list => Array.isArray(list) && list.some(t => t)) ?? []) as string[];
      if (fillMissingStepHeadings(this.report, titles)) this.invalidateReportCaches();
    });
  }

  /**
   * Trägt die Regel-Metadaten (Kürzel + Beschreibung) in einen konkreten Bericht ein – bei
   * gemischten Läufen je Teilbericht, sonst in {@link report}.
   */
  private applyRuleMetadataToReport(
    report: NormalizedReport | null, metaByName: Map<string, RuleMetadata>,
  ): void {
    if (!report) return;
    const applyTo = (rules: ReportRule[] | undefined) =>
      (rules ?? []).forEach(rule => {
        const meta = metaByName.get(ruleMetadataKey(rule.name));
        if (meta?.code) rule.shortCode = meta.code;
        if (meta?.description) rule.description = meta.description;
      });
    for (const group of report.groups ?? []) {
      applyTo(group.rules);
      for (const structure of group.structures ?? []) applyTo(structure.rules);
    }
    // Kürzel/Beschreibung landen am bestehenden Berichtsobjekt – die Identität ändert sich dabei
    // nicht, deshalb müssen die Ableitungen hier explizit verworfen werden.
    this.invalidateReportCaches();
  }

  /** Löst den Slug des Identifikator-Parameters aus einer (der ersten) Regel-Definition auf. */
  private resolveHybridIdentifikatorSlug(
    children: WorkflowInstanceDto[], projectId: number,
  ): Observable<string | undefined> {
    const ruleDefId = children.find(child => child?.definitionId)?.definitionId;
    if (!ruleDefId) return of(undefined);
    return this.workflowService.getDefinition(ruleDefId, projectId).pipe(
      map(definition => this.extractIdentifikatorSlug(definition)),
      catchError(() => of(undefined)),
    );
  }

  private extractIdentifikatorSlug(definition: any): string | undefined {
    let manifest: any = {};
    try {
      manifest = definition?.manifestJson ? JSON.parse(definition.manifestJson) : (definition?.manifest ?? {});
    } catch {
      manifest = {};
    }
    const config = manifest?.trigger?.config ?? {};
    const params = Array.isArray(config.parameter) ? config.parameter : [];
    const identParam = params.find((p: any) => p?.id === config.identifikatorParamId) ?? params[0];
    return identParam?.name ? slugKey(identParam.name, new Set<string>()) : undefined;
  }

  private loadReport(id: string, type?: string): void {
    // State zurücksetzen, damit bei einem Routenwechsel (Component wird wiederverwendet) der
    // Spinner erscheint statt des zuvor geladenen (alten) Berichts.
    this.loading = true;
    this.report = null;
    this.reportDisplayConfig = null;
    this.rulesetDisplayName = '';
    this.rulesetLinks = [];
    this.readOnlyRulesetIds.clear();
    this.myCheckDefinitionId = undefined;
    this.myCheckIsAutoGenerated = false;
    this.myCheckIsReadOnly = false;
    // Vorbelegt mit dem aus der Berichtsliste übergebenen Namen, damit das „myCheck"-Feld nie auf
    // den myCheck-Namen zurückfällt, während/falls die Workflow-Definition (asynchron) nicht
    // auflösbar ist (z. B. IDS-CheckResults ohne workflowInstanceId).
    this.myCheckDisplayName = this.myCheckNameFromRoute ?? '';
    this.reportRunName = '';
    this.idsThresholds = null;
    this.workflowStatus = undefined;
    this.columnSelection = {};
    this.selectedGroup = undefined;
    this.selectedStructure = undefined;
    this.selectedRule = undefined;
    this.mixedEntries = [];
    this.activeMixedIndex = 0;
    this.reportProjectFiles = [];
    this.projectFileIdsByName.clear();
    this.uniqueOpenableFileId = null;

    const projectId = this.stateConfigService.getProjectId()!;
    const normalizedType = type?.toUpperCase();

    if (normalizedType === 'MIXED') {
      // Gemischter Lauf: die Teilberichte liegen in der Workflow-Instanz (outputData.reports[]),
      // die ID ist deshalb eine workflowInstanceId.
      this.loadFromInstance(id, projectId, normalizedType);
    } else if (normalizedType === 'HYBRID' || normalizedType === 'AI'
      || normalizedType === 'HOAI' || normalizedType === 'KI') {
      // Hybrid/AI (inkl. Alt-Bezeichnungen): ID ist eine workflowInstanceId → direkt aus Instanz laden.
      this.loadFromInstance(id, projectId, normalizedType);
    } else if (normalizedType === 'IDS') {
      // IDS: ID is a CheckResult ID
      this.checkResultService.getCheckResult(id, projectId).subscribe({
        next: (result: CheckResult) => {
          if (result) {
            this.handleCheckResult(result, normalizedType);
          } else {
            this.loading = false;
          }
        },
        error: () => {
          // Fallback to instance if CheckResult not found. Kein erneuter CheckResult-Versuch –
          // der ist hier gerade fehlgeschlagen.
          this.loadFromInstance(id, projectId, normalizedType, false);
        }
      });
    } else {
      // Unknown type: try CheckResult first, fallback to instance
      this.checkResultService.getCheckResult(id, projectId).subscribe({
        next: (result: CheckResult) => {
          if (result) {
            this.handleCheckResult(result, normalizedType);
          } else {
            this.loadFromInstance(id, projectId, normalizedType, false);
          }
        },
        error: () => {
          this.loadFromInstance(id, projectId, normalizedType, false);
        }
      });
    }
  }

  /**
   * Baut aus `outputData.reports[]` je Regelset einen fertig normalisierten Teilbericht.
   *
   * Jeder Eintrag behält das typspezifische Format seines Sub-Runs, deshalb wird der bestehende
   * Adapter je Eintrag mit dessen eigenem `check_type` instanziiert – die Adapter sind zustandslos
   * und arbeiten auf rohem JSON, es ist keine Änderung an ihnen nötig.
   */
  private buildMixedReports(
    outputData: any,
    meta: { checkerName?: string; checkDate?: any; definitionId?: string },
    projectId: number,
  ): void {
    const rawEntries: any[] = Array.isArray(outputData?.['reports']) ? outputData['reports'] : [];

    this.mixedEntries = rawEntries.map((entry) => {
      const checkType = String(entry?.check_type ?? entry?.checkType ?? 'IDS').toUpperCase();
      const rulesetName = entry?.ruleset_name ?? entry?.rulesetName ?? 'myCheck';
      const childReport = entry?.report;

      let report: NormalizedReport | null = null;
      if (childReport && typeof childReport === 'object') {
        // Metadaten wie beim Einzellauf ergänzen; sie stehen am Gesamtlauf, nicht im Teilbericht.
        const rawData = {
          ...childReport,
          ruleset_name: rulesetName,
          checker_name: meta.checkerName ?? childReport['checker_name'] ?? '',
          check_date: meta.checkDate,
        };
        report = getReportAdapter(checkType).parseReportData(rawData);
      }

      return {
        rulesetId: entry?.ruleset_id ?? entry?.rulesetId,
        rulesetName,
        ruleCount: typeof (entry?.rule_count ?? entry?.ruleCount) === 'number'
          ? entry.rule_count ?? entry.ruleCount
          : undefined,
        checkType,
        status: entry?.status,
        childInstanceId: entry?.child_instance_id ?? entry?.childInstanceId,
        error: entry?.error,
        report,
        rawReport: childReport,
        // Bevorzugt der eingefrorene Snapshot des Sub-Runs; die Definition dient als Fallback
        // und wird unten für alle Einträge in einem Zug nachgeladen.
        displayConfig: resolveReportDisplayConfigFromSnapshot(childReport, {
          checkType,
          selection: {selectedRuleSetIds: entry?.ruleset_id ? [entry.ruleset_id] : []},
        }),
      } satisfies MixedReportEntry;
    });

    this.selectMixedEntry(0);
    this.loading = false;
    this.correctTypeQueryParam();
    this.loadMixedRuleCounts(projectId);

    if (this.mixedEntries.some(entry => !entry.displayConfig)) {
      this.applyMixedDisplayConfigsFromDefinition(meta.definitionId, projectId);
    }

    this.enrichHybridMixedEntries(meta, projectId);
    this.applyMixedRuleMetadata(projectId);
  }

  private loadReportConfigFromWorkflowInstance(instanceId?: string, checkType?: string, onDone?: () => void): void {
    if (!instanceId) {
      // Keine Workflow-Instanz auflösbar (z. B. IDS-CheckResult ohne workflowInstanceId) – der in
      // loadReport() vorbelegte myCheckNameFromRoute bleibt bestehen statt auf den
      // myCheck-Namen zurückzufallen.
      this.reportDisplayConfig = null;
      this.updateAutoSelections();
      onDone?.();
      return;
    }

    const projectId = this.stateConfigService.getProjectId()!;
    this.instanceService.getInstance(instanceId, projectId).subscribe({
      next: (instance) => {
        this.loadMyCheckDisplayName(instance.definitionId, instance.workflowName, runReportName(instance));
        this.applyReportDisplayConfigFromInstance(instance, checkType, onDone);
      },
      error: () => {
        this.reportDisplayConfig = null;
        this.updateAutoSelections();
        onDone?.();
      },
    });
  }

  /**
   * Setzt die Anzeige-Config bevorzugt aus dem eingefrorenen Lauf-Snapshot (`instance.outputData`),
   * damit ein bereits gelaufener Bericht stabil bleibt, auch wenn die myCheck-Einstellungen später
   * geändert werden. Nur wenn der Lauf keinen Snapshot trägt (Alt-Läufe), wird auf die aktuelle
   * Definition zurückgegriffen.
   */
  private applyReportDisplayConfigFromInstance(instance: WorkflowInstanceDto, checkType?: string, onDone?: () => void): void {
    const snapshot = resolveReportDisplayConfigFromSnapshot(instance?.outputData, {checkType});
    if (snapshot) {
      this.reportDisplayConfig = snapshot;
      this.updateAutoSelections();
      // `rule_groups` im Instanz-Output (siehe applyOverviewSelection) liefert den
      // myCheck-Namen nur bei Mehrfach-Regelset-Läufen (Hybrid). Einzelregelset-Läufe
      // (KI/HOAI) haben dort nichts – ohne diesen Fallback bliebe „myCheck" für sie leer,
      // weil dieser Zweig sonst nie die Definition (und damit deren Regelset-Auswahl) lädt.
      if (!this.rulesetDisplayName) {
        this.loadRulesetDisplayNameFromDefinition(instance?.definitionId);
      }

      onDone?.();
      return;
    }
    this.loadReportConfigFromDefinition(instance?.definitionId, checkType, onDone);
  }

  private loadReportConfigFromDefinition(definitionId?: string, checkType?: string, onDone?: () => void): void {
    if (!definitionId) {
      this.reportDisplayConfig = null;
      this.updateAutoSelections();
      onDone?.();
      return;
    }

    const projectId = this.stateConfigService.getProjectId()!;
    this.workflowService.getDefinition(definitionId, projectId).subscribe({
      next: (definition) => {
        this.reportDisplayConfig = resolveReportDisplayConfig(definition.manifestJson, {checkType});
        this.resolveRulesetDisplayName(definition.manifestJson, projectId);
        this.updateAutoSelections();
        onDone?.();
      },
      error: () => {
        this.reportDisplayConfig = null;
        this.updateAutoSelections();
        onDone?.();
      },
    });
  }

  /** Der Name der Workflow-Definition ist der myCheck-Name; Instanznamen können Sub-Run-Regelsets sein. */
  private loadMyCheckDisplayName(definitionId: string | undefined, fallbackName: string | undefined,
                                 reportName?: string): void {
    this.reportRunName = reportName?.trim() ?? '';
    this.myCheckDisplayName = fallbackName ?? this.myCheckNameFromRoute ?? '';
    this.myCheckDefinitionId = definitionId;
    if (!definitionId) return;
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;
    this.workflowService.getDefinition(definitionId, projectId).subscribe({
      next: definition => {
        this.myCheckDisplayName = definition.name?.trim() || fallbackName || this.myCheckNameFromRoute || '';
        this.myCheckIsAutoGenerated = !!(definition as any).autoForRulesetId;
        this.myCheckIsReadOnly = !!definition.readOnly;
      },
      error: () => undefined,
    });
  }

  /**
   * Merkt sich, welche Regelwerke bereitgestellt (read-only) sind.
   *
   * `readOnly` wird im `RulesetService` je Typ aus dem Backend-Flag bzw. hilfsweise aus
   * `isPrebuilt`/`projectId === null` abgeleitet – hier wird beides berücksichtigt, damit auch
   * ein vorgefertigtes Set ohne explizites Flag nicht verlinkt wird.
   */
  private collectReadOnlyRulesetIds(rulesets: Ruleset[]): void {
    for (const ruleset of rulesets) {
      if (ruleset.readOnly || ruleset.isPrebuilt) this.readOnlyRulesetIds.add(ruleset.id);
    }
  }

  /** Ordnet den Prüfregeltyp eines Mixed-Teilberichts der Regelset-Editor-Route zu. */
  private rulesetTypeFromCheckType(checkType: string): RulesetType {
    const normalized = (checkType ?? '').toUpperCase();
    if (normalized === 'IDS') return 'IDS';
    if (normalized === 'HYBRID') return 'HYBRID';
    return 'KI';
  }

  /** Lädt nur den myCheck-Anzeigenamen aus der Definition nach (siehe {@link applyReportDisplayConfigFromInstance}). */
  private loadRulesetDisplayNameFromDefinition(definitionId: string | undefined): void {
    if (!definitionId) return;
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;
    this.workflowService.getDefinition(definitionId, projectId).subscribe({
      next: (definition) => this.resolveRulesetDisplayName(definition.manifestJson, projectId),
      error: () => undefined,
    });
  }

  /**
   * Ermittelt den Anzeigenamen des/der gewählten myCheck(s) aus dem Definition-Manifest
   * (`rule_selection.selectedRuleSetIds`) und mappt die IDs über den RulesetService auf Namen.
   * Speist den Overview-Header (`info2` = „myCheck"). Best-effort: bleibt leer, wenn nichts auflösbar ist.
   */
  /**
   * Färbt den IDS-Bericht nach den Schwellwerten seines Prüfregelsets (#2309).
   *
   * <p>Wird von mehreren Ladepfaden aufgerufen (Instanzlauf, Teilbericht eines gemischten Laufs)
   * und ist deshalb absichtlich idempotent und anspruchslos: fehlt der Bericht, das Regelset oder
   * die Konfiguration, passiert nichts und die Färbung bleibt die bisherige.
   *
   * <p>Warum nachträglich und nicht im Adapter: die Schwellwerte hängen am Prüfregelset, dessen ID
   * erst asynchron feststeht — zu diesem Zeitpunkt ist der Bericht längst geparst. Dasselbe Muster
   * nutzt {@link applyRuleMetadataToReport} für Kürzel und Beschreibungen.
   */
  private applyIdsThresholds(projectId: number | undefined): void {
    if (!this.isIdsReport || !this.report || !projectId) return;
    const rulesetId = this.rulesetLinks.find(link => link.type === 'IDS')?.id;
    if (!rulesetId) return;

    const report = this.report;
    this.rulesetService.getIdsRulesetThresholds(rulesetId, projectId).subscribe({
      next: thresholds => {
        // Der Nutzer kann zwischenzeitlich einen anderen Teilbericht gewählt haben.
        if (!thresholds || this.report !== report) return;
        this.idsThresholds = thresholds;
        applyIdsThresholdsToReport(report, thresholds);
        // Die Farben landen am bestehenden Berichtsobjekt – die Identität ändert sich dabei nicht,
        // deshalb müssen die Ableitungen hier explizit verworfen werden.
        this.invalidateReportCaches();
      },
      error: () => undefined,
    });
  }

  private resolveRulesetDisplayName(manifestJson: string | undefined, projectId: number): void {
    const selection = extractReportSelectionFromManifest(manifestJson);
    const setIds = new Set(selection.selectedRuleSetIds);
    if (setIds.size === 0) return;

    this.rulesetService.listRulesets(projectId).subscribe({
      next: ({prebuilt, custom}) => {
        this.collectReadOnlyRulesetIds([...prebuilt, ...custom]);
        const byId = new Map([...prebuilt, ...custom].map(ruleset => [ruleset.id, ruleset]));
        const rulesets = [...setIds]
          .map(id => byId.get(id))
          .filter((ruleset): ruleset is Ruleset => !!ruleset);
        if (rulesets.length > 0) {
          this.rulesetDisplayName = rulesets.map(ruleset => ruleset.name).join(', ');
          this.rulesetLinks = rulesets.map(ruleset => ({id: ruleset.id, name: ruleset.name, type: ruleset.type}));
          this.applyIdsThresholds(projectId);
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Die während der Ausführung aufgelöste Auswahl ist maßgeblich: bei manueller Auswahl kann
   * sie von der im Manifest hinterlegten Vorlage abweichen. Damit stehen Regelset und Regelanzahl
   * in der Overview auch für solche Läufe korrekt da.
   */
  private applyOverviewSelection(outputData: Record<string, any>): void {
    const groups = Array.isArray(outputData['rule_groups']) ? outputData['rule_groups'] : [];
    if (groups.length === 0) return;

    const names = groups
      .map((group: any) => group?.ruleset_name ?? group?.rulesetName)
      .filter((name: unknown): name is string => typeof name === 'string' && name.trim().length > 0);
    if (names.length > 0) {
      this.rulesetDisplayName = [...new Set(names)].join(', ');
    }
  }

  private updateAutoSelections(): void {
    if (!this.report) return;

    // IDS: 2-Spalten-Master-Detail – erste Spezifikation automatisch vorselektieren.
    if (this.isIdsReport) {
      this.selectedGroup = this.report.groups[0];
      this.selectedRule = this.selectedGroup?.rules?.[0];
      return;
    }

    // Generischer Renderer (KI/Hybrid): je Spalte das erste Item vorselektieren.
    this.autoSelectColumns();
  }

  private isColumnVisible(levelKey: string, columnKey: string, defaultVisible: boolean): boolean {
    return isColumnVisible(this.reportDisplayConfig, levelKey, columnKey, defaultVisible);
  }

  private normalizeLevelKey(levelKey: string): 'files' | 'rules' | 'detail' | null {
    if (levelKey === 'files' || levelKey === 'structure') return 'files';
    if (levelKey === 'rules') return 'rules';
    if (levelKey === 'detail') return 'detail';
    return null;
  }

  private resolveOpenFileId(): string | null {
    const selectedFileName = this.columnSelection['files'];
    const fromSelectedColumn = this.projectFileIdByName(selectedFileName);
    if (fromSelectedColumn) return fromSelectedColumn;

    const fromSelectedGroup = this.projectFileIdByName(this.selectedGroup?.name);
    if (fromSelectedGroup) return fromSelectedGroup;

    if (this.report?.meta.fileCount === 1) {
      const fromMeta = this.projectFileIdByName(this.report.meta.fileName);
      if (fromMeta) return fromMeta;
    }

    return this.uniqueOpenableFileId;
  }

  private projectFileIdByName(name: string | undefined): string | null {
    const normalized = this.normalizeFileName(name);
    if (!normalized) return null;
    return this.projectFileIdsByName.get(normalized) ?? null;
  }

  private registerProjectFilesFromPayload(payload: any): void {
    if (!payload || typeof payload !== 'object') return;
    this.registerProjectFiles(payload['project_files']);
    this.registerProjectFiles(payload['projectFiles']);
    this.registerProjectFiles(payload['files']);
    this.registerProjectFiles([payload['file']]);
    this.registerProjectFiles(payload['source_files']);
    if (Array.isArray(payload['results'])) {
      payload['results'].forEach((entry: any) => this.registerProjectFilesFromPayload(entry));
    }
    if (Array.isArray(payload['reports'])) {
      payload['reports'].forEach((entry: any) => this.registerProjectFilesFromPayload(entry?.report ?? entry));
    }
    if (payload['outputs'] && typeof payload['outputs'] === 'object') {
      this.registerProjectFilesFromPayload(payload['outputs']);
    }
    if (Array.isArray(payload['answers'])) {
      payload['answers'].forEach((entry: any) => this.registerProjectFilesFromPayload(entry));
    }
  }

  private registerProjectFiles(files: any): void {
    if (!Array.isArray(files)) return;
    const uniqueIds = new Set<string>();
    this.projectFileIdsByName.forEach(id => uniqueIds.add(id));

    for (const file of files) {
      if (!file || typeof file !== 'object') continue;
      const id = this.normalizeFileId(file.id);
      const name = this.extractFileName(file);
      if (!id || !name) continue;
      this.projectFileIdsByName.set(name, id);
      uniqueIds.add(id);
    }

    this.uniqueOpenableFileId = uniqueIds.size === 1 ? [...uniqueIds][0] : null;
  }

  private extractFileName(file: any): string | null {
    const rawName = file.name ?? file.readable_name ?? file.file_name ?? file.fileName ?? file.filename;
    return this.normalizeFileName(rawName);
  }

  private normalizeFileName(name: unknown): string | null {
    if (typeof name !== 'string') return null;
    const normalized = name.trim().toLocaleLowerCase('de-DE');
    return normalized ? normalized : null;
  }

  private normalizeFileId(id: unknown): string | null {
    if (typeof id === 'number') return String(id);
    if (typeof id !== 'string') return null;
    const normalized = id.trim();
    return normalized ? normalized : null;
  }
}
